/**
 * The Trip Planner: S08 -> S08b -> S10 -> S12.
 *
 * Implements the planning flow from docs/TripPool_UI_UX_Design_Specification_v0.2.md:
 * - Step 1: Trip setup with sensible defaults (S08).
 * - Step 2: Place selection, Map-first (S08b).
 * - Step 3: The proposed itinerary, read and confirmed (S10).
 * - Step 4: Selected roadmap with interactive timeline & map (S12).
 *
 * The constraint step (S09) is gone; `copy.planner.steps` carries the reason, and why `TripConstraints`
 * keeps the fields the step used to offer.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, ReactElement } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { PlanRoadmap } from "../components/PlanRoadmap.tsx";
import { RangeCalendar } from "../components/RangeCalendar.tsx";
import { RouteMap, type RouteMapLeg, type RouteMapStop } from "../components/RouteMap.tsx";
import { readToken } from "../lib/tokens.ts";

import { ApiError } from "@trippool/api-client";
import { useMutation } from "@tanstack/react-query";
import type { TripResponse } from "@trippool/contracts";
import type {
  PlaceCategory,
  PlanExplanation,
  PlanVariant,
  PlanVariantDetail,
  GeneratedPlanVariant,
  ProviderStatus,
  TransportMode,
  TripSlot,
} from "@trippool/contracts";

import { categoryGlyph, copy } from "../copy.ts";
import { useGoBack } from "../lib/useGoBack.ts";
import { messageFor } from "../lib/errors.ts";
import {
  formatDriving,
  formatDrivingMinutes,
  formatKm,
  formatRestHours,
  stopMarkers,
} from "../lib/plan.ts";
import { assembleArrangeDay, type ArrangeDayStop, type ArrangePlace } from "../lib/arrangeDay.ts";
import { reorderedArrangement, standingUndo, suggestOrder, type SuggestionUndo } from "../lib/arrangeOrder.ts";
import { autoArrangeDays } from "../lib/autoArrange.ts";
import { dropIndexAt, moveItem } from "../lib/dragOrder.ts";
import { displayName } from "../lib/placeName.ts";
import { readTripAnchor, writeTripAnchor } from "../lib/tripAnchor.ts";
import { tripDateRange } from "../lib/tripDates.ts";
import { groupOf, groupPlaces } from "../lib/placeGroups.ts";
import { tileTintOf } from "../components/PlaceCard.tsx";
import { haversineMetres, slotsSuitedBy } from "@trippool/core";
import { useAreas, usePlaces, useRoutePreview, useTrip } from "../lib/queries.ts";
import { useCurrentUser, useSessionManager } from "../lib/session-context.tsx";

// Default weekend calculation: next Saturday to Monday (3 days, 2 nights)
function defaultDates(): { start: string; end: string } {
  const now = new Date();
  const day = now.getDay();
  const toSaturday = (6 - day + 7) % 7 || 7;
  const start = new Date(now);
  start.setDate(now.getDate() + toSaturday);
  const end = new Date(start);
  end.setDate(start.getDate() + 2);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
}

/** The shape the planner saves to sessionStorage and reads back at mount. */
interface PlannerDraft {
  step?: number;
  destination?: string;
  startDate?: string;
  endDate?: string;
  adults?: number;
  children?: number;
  transport?: "car" | "motorbike" | "walk";
  selectedPlaceIds?: string[];
  pace?: "relaxed" | "balanced" | "packed";
  /** The user's own words for an unnamed mode, and whether they are describing one at all. */
  transportNote?: string;
  transportIsOther?: boolean;
  /** Nights are days - 1 unless the trip stays the last one. */
  stayLastNight?: boolean;
  /**
   * The arrangement and its times, as **unsaved work**.
   *
   * The trip is where an arrangement lives and the draft is where work in progress lives — the division
   * `selectedPlaceIds` already follows, and it was missing here: measured, arranging places and reloading
   * the page lost them, because nothing had written them to a trip yet. Not a second authority; the same
   * arrangement, before it has been saved.
   */
  arrangement?: Record<string, string[]>;
  slots?: Record<string, TripSlot>;
  /** The trip the arrangement was last saved to, so a reload continues it instead of creating another. */
  arrangedTripId?: string | null;
  /** Which lodgings the trip uses, so a reload does not fall back to asking night by night. */
  lodgingChoices?: string[];
  /** The days the party comes back to rest. */
  restDays?: number[];
  /**
   * The destination area.
   *
   * Not derivable from anything else in the draft, and the candidates are derived *from* it — so leaving it
   * out of the draft meant a reload lost the whole place list: measured, the arrange step came back with no
   * rows and "Cả 0 địa điểm đã được xếp", while the day chips still held the arrangement that named eleven.
   */
  destinationAreaId?: string;
  /** Night date → lodging place id, or absent for an unassigned night. */
  nightLodgings?: Record<string, string | undefined>;
  entryPlaceId?: string | null;
}

/** The user's stated pace points at the plan most likely to match it; a nudge, not a fact. */
/**
 * "28/9" — the day and month, which is how a night is named out loud.
 *
 * Not `toLocaleDateString`: the app has one date format and it is written here. A locale call renders
 * differently on a device set to another region, so two people comparing the same trip over the phone would
 * read different dates off the same row.
 */
function formatShortDate(date: string): string {
  const [, month, day] = date.split("-");
  return `${Number(day)}/${Number(month)}`;
}

const PACE_TO_STRATEGY: Record<"relaxed" | "balanced" | "packed", PlanVariant["strategy"]> = {
  relaxed: "relax",
  balanced: "balanced",
  packed: "explore",
};

/**
 * The shape the map and list below read, from the one source there is: a saved place (`PlaceSummary`,
 * keyed by `id`). The contract type keeps coordinates at `location.lat`/`lon`, so it is mapped here
 * rather than every render site knowing contract spelling.
 */
interface PlannerPlace {
  id: string;
  name: string;
  lat: number | null;
  lng: number | null;
  /**
   * What kind of place it is, which is what the picker groups by.
   *
   * Carried through from the place rather than looked up: a saved place reports `categories`, so a
   * second read would be a request for something already in hand.
   */
  categories: PlaceCategory[];
}


const DRAFT_STORAGE_KEY = "TRIPPOOL_PLANNER_DRAFT";

/**
 * The steps, read from `copy.planner.steps` so the rail and the rest of the copy cannot drift.
 *
 * Named for what the user does in them rather than for what the code builds, and short because all of them
 * share a phone's width. The constraint step is gone; `copy.planner.steps` says why, and why the fields it
 * used to offer survive in the contract.
 */
const PLANNER_STEPS = copy.planner.steps;

/**
 * How large a party the counters allow.
 *
 * Twenty, which is what the two selects offered before them — a busload is not what this is for, and a
 * ceiling is what makes the plus button able to be disabled rather than able to be pressed forever.
 */
const MAX_ADULTS = 20;
const MAX_CHILDREN = 20;

/**
 * The class per step state, as literals rather than as an interpolated suffix.
 *
 * `apps/web/src/styles/styles.test.ts` scans this file for the strings its stylesheets style, so a class
 * assembled from a template is a class nothing can find — and the rule for it reads as dead. Steps still
 * to come have no modifier: the base rule is their look, and a class with no rule behind it is worse.
 */
const STEP_ITEM_CLASS = {
  done: "planner-steps__item planner-steps__item--done",
  current: "planner-steps__item planner-steps__item--current",
  todo: "planner-steps__item",
} as const;

/**
 * The planner's sticky chrome heights, written to custom properties on its screen.
 *
 * The day rail and the arrange map stick below that header, so both need its height to clear it — and the
 * map sits below the day rail as well, so it needs the rail's height too. Neither is a constant: the head
 * measures one height at 390px and another at 1024px, and the rail's height moves with its own wrapping.
 * Both are therefore read from the elements rather than fixed in the stylesheet. Called when the head
 * attaches and on every viewport resize; a no-op before it exists.
 *
 * The pinned action bar's height is published for the same reason, on its other edge. It is the one piece of
 * chrome that sits *over* the scroll rather than above it, so the content beneath it has to reserve its
 * strip: the bar measures 77px with no warning and 109px once "Còn N ngày trống" wraps onto its own row, and
 * the phone adds the home-indicator inset to its padding — so a constant in the stylesheet would be a stale
 * copy of a layout fact, exactly like the head and the rail. The measured height already includes that
 * `env(safe-area-inset-bottom)` padding, so the reserve below it does not add the inset a second time.
 */
function publishStickyHeights(head: HTMLDivElement | null): void {
  const screen = head?.closest(".planner-screen");
  if (head === null || !(screen instanceof HTMLElement)) return;
  const headH = `${head.getBoundingClientRect().height}px`;
  screen.style.setProperty("--planner-head-h", headH);
  document.documentElement.style.setProperty("--planner-head-h", headH);
  const rail = screen.querySelector(".daytabs");
  const railH = `${rail === null ? 0 : rail.getBoundingClientRect().height}px`;
  screen.style.setProperty("--planner-daytabs-h", railH);
  document.documentElement.style.setProperty("--planner-daytabs-h", railH);
  // Only the pinned bar: a `savebar--static` sits in the flow and covers nothing.
  const bar = screen.querySelector(".savebar:not(.savebar--static)");
  const barH = `${bar === null ? 0 : bar.getBoundingClientRect().height}px`;
  screen.style.setProperty("--planner-savebar-h", barH);
  document.documentElement.style.setProperty("--planner-savebar-h", barH);
}

export function PlannerScreen(): ReactElement {
  const user = useCurrentUser();
  const userId = user?.id ?? "";
  const navigate = useNavigate();
  const [params] = useSearchParams();
  /**
   * The trip being edited, from the route.
   *
   * Undefined on `/trips/new`, which is how one screen serves both: the difference between making a trip and
   * changing one is entirely in the seeding below and in which id the writes target, not in the form.
   */
  const { tripId: editingTripId = "" } = useParams<{ tripId: string }>();
  const isEditing = editingTripId !== "";
  const editingQuery = useTrip(userId, editingTripId);
  /**
   * A draft per mode, because the two are different pieces of work.
   *
   * Sharing one key meant an edit wrote its state into the draft a *new* trip would then restore: make a new
   * trip after editing one, and the form arrives holding the other trip's dates, places and arrangement. Two
   * keys, and each mode restores what it was doing.
   */
  const draftKey = useMemo(
    // Stable across renders: the auto-save effect further down depends on it, and a fresh template string
    // each render made that dependency meaningless.
    () => (isEditing ? `${DRAFT_STORAGE_KEY}_EDIT_${editingTripId}` : DRAFT_STORAGE_KEY),
    [isEditing, editingTripId],
  );

  /**
   * The draft, read once at mount.
   *
   * Read once and not on every render: the auto-save below writes it as the user types, so a second read
   * would race the first write and restore what had just been saved over.
   */
  const [initialDraft] = useState<PlannerDraft | null>(() => {
    if (typeof window === "undefined") return null;
    const saved = window.sessionStorage?.getItem(draftKey);
    if (!saved) return null;
    try {
      return JSON.parse(saved) as PlannerDraft;
    } catch {
      // A draft that no longer parses is not a draft; it must not white-screen the planner.
      return null;
    }
  });

  /*
   * The area the trip goes to, from the URL when a card opened this screen.
   *
   * `?areaId=` is what narrows the candidates to one area. Without it, opening this screen seeds the
   * selection with **every** place the user ever saved, which is not "plan a trip around Đà Lạt".
   */
  const areaId = params.get("areaId") ?? "";
  const { client } = useSessionManager();

  const areasQuery = useAreas(userId);
  /*
   * Every saved place, unfiltered.
   *
   * Unfiltered because the destination is now chosen here rather than in the query: the screen has to offer
   * each area with its place count, and it cannot count what it did not fetch. One request either way, and
   * `usePlaces` keys on the filter so nothing else collides with it in the cache.
   */
  /**
   * The destination area, from the draft, from the URL when a card opened this screen, or empty.
   *
   * `?areaId=` is the ordinary way in rather than a special case: an area *is* where a trip goes.
   */
  const [destinationAreaId, setDestinationAreaId] = useState<string>(
    initialDraft?.destinationAreaId ?? areaId,
  );

  const allPlacesQuery = usePlaces(userId, {});
  const savedPlaces = useMemo(() => allPlacesQuery.data?.items ?? [], [allPlacesQuery.data]);

  const areas = useMemo(
    () =>
      (areasQuery.data?.areas ?? []).map((area) => ({
        id: area.id,
        name: area.name,
        count: savedPlaces.filter((place) => place.areas.some((own) => own.id === area.id)).length,
      })),
    [areasQuery.data, savedPlaces],
  );

  /**
   * The candidates: the places filed under the chosen destination.
   *
   * Derived and not ticked. A trip goes to an area, and the places in it are what it can be planned from —
   * which is what replaced the selection step. Choosing a subset is now the arrangement's job, where leaving
   * a place off every day means exactly that and the count of what is left out sits on the same screen.
   */
  const rawPlaces = useMemo(
    (): PlannerPlace[] =>
      destinationAreaId === ""
        ? []
        : savedPlaces
            .filter((place) => place.areas.some((area) => area.id === destinationAreaId))
            .map((place) => ({
              id: place.id,
              name: place.name,
              lat: place.location?.lat ?? null,
              lng: place.location?.lon ?? null,
              categories: [...place.categories],
            })),
    [destinationAreaId, savedPlaces],
  );

  /**
   * Places with no area cannot be reached by a destination, which is a fact worth stating.
   *
   * The destination is an area; a place filed under none is in no area, so no destination brings it in. Saying
   * how many there are — with where to fix it — is the difference between a limitation and a place the user
   * cannot find.
   */
  const unplacedByArea = savedPlaces.filter((place) => place.areas.length === 0).length;

  /** What the trip plans with: every place in its destination. */
  const selectedPlaceIds = useMemo(() => new Set(rawPlaces.map((place) => place.id)), [rawPlaces]);


  // Current step: 1 = Setup, 2 = Arrange the days, 3 = Review & confirm, 4 = Roadmap
  const [step, setStep] = useState<1 | 2 | 3 | 4>(
    // Plans and the trip they belong to live server-side under the trip id, which the draft does
    // not carry, so a restored draft is only trusted up to the last step whose inputs it stores.
    // Clamped to 2 — the arrangement, now the last step whose inputs the draft carries. The review and
    // roadmap steps are drawn from server-side plans the draft never held.
    (Math.min(initialDraft?.step ?? 1, 2) as 1 | 2),
  );

  // ---- Step 1 State: Trip Setup
  const dates = defaultDates();
  const [destination, setDestination] = useState<string>(initialDraft?.destination ?? "");
  const [startDate, setStartDate] = useState(initialDraft?.startDate ?? dates.start);
  const [endDate, setEndDate] = useState(initialDraft?.endDate ?? dates.end);
  const [adults, setAdults] = useState(initialDraft?.adults ?? 2);
  const [children, setChildren] = useState(initialDraft?.children ?? 2);
  const [transport, setTransport] = useState<TransportMode>(initialDraft?.transport ?? "car");
  /*
   * "Khác" is a state of its own, not a sixth enum member.
   *
   * `TransportMode` is closed and the planner needs it closed — it picks a speed, and a speed cannot be
   * read out of free text. So choosing "Khác" stores the enum's own catch-all (`public_transport`, whose
   * contract comment says the extra members "exist so the enum is not a lie") and keeps the user's words
   * beside it in `transport_note`. The flag is what distinguishes the two ways of arriving at that
   * member, so the screen can show back which one was chosen.
   */
  const [transportIsOther, setTransportIsOther] = useState<boolean>(initialDraft?.transportIsOther ?? false);
  const [transportNote, setTransportNote] = useState<string>(initialDraft?.transportNote ?? "");
  const [pace, setPace] = useState<"relaxed" | "balanced" | "packed">(initialDraft?.pace ?? "balanced");
  /*
   * Nights, entered here rather than derived silently.
   *
   * "2 ngày 1 đêm" and "2 ngày 2 đêm" are different trips and a Vietnamese traveller says which, so the
   * count is a visible choice with a default rather than arithmetic behind the screen. The dates give the
   * default: you sleep between the days and go home on the last one.
   */
  const [stayLastNight, setStayLastNight] = useState<boolean>(initialDraft?.stayLastNight ?? false);
  /**
   * Which night is spent where, keyed by `YYYY-MM-DD`.
   *
   * A record rather than a list of pairs, and keyed by date rather than by index — a night is identified
   * by the date the user says ("đêm 28/9"), and the same key is what makes the assignment survive the trip's
   * dates changing: nights that survive keep their lodging, nights that no longer exist are dropped, and
   * new ones arrive empty. See the reconcile effect below.
   */
  const [nightLodgings, setNightLodgings] = useState<Record<string, string | undefined>>(
    initialDraft?.nightLodgings ?? {},
  );
  /**
   * Which lodgings this trip uses at all.
   *
   * A different question from which night is spent where, and the reason the per-night control exists only when
   * it is needed: with one lodging there is nothing to assign, so the trip sleeps there every night and the
   * screen says so. Asking night by night for a single answer is a question with one option, and it reads as
   * though the app expects the party to move.
   */
  const [lodgingChoices, setLodgingChoices] = useState<string[]>(initialDraft?.lodgingChoices ?? []);
  /**
   * The days the party drives back to its lodging to rest.
   *
   * A day and not a trip, because a day spent driving to the far side of the province rests where it stops —
   * and the answer is the same on most days, so this is a list of the exceptions and most trips touch nothing.
   */
  const [restDays, setRestDays] = useState<number[]>(initialDraft?.restDays ?? []);
  /**
   * The user's arrangement: day index → the places on it, in the order they put them there.
   *
   * A record of lists rather than a place → day map, because **the same place may be on several days** and a
   * map from place to day cannot say that. The list per day also keeps the order the user placed them in,
   * which is the order the day's route starts from.
   *
   * Not in the draft: the arrangement is stored on the trip, so a copy in the browser would be a second
   * version of something the server owns, and the two would disagree after any edit from elsewhere.
   */
  const [arrangement, setArrangement] = useState<Record<number, string[]>>(
    // JSON keys are strings; the render reads them by the same string form the setters write.
    (initialDraft?.arrangement ?? {}) as Record<number, string[]>,
  );
  /**
   * The time of day for a place on a day, keyed `"<date>|<placeId>"`.
   *
   * Keyed by both, not by place alone: the same café on two mornings is one place and two decisions, and a
   * map from place to slot would force them to agree. Absent means the user said nothing, which the engine
   * treats as flexible and a screen has to tell apart from `cả ngày`.
   */
  const [slots, setSlots] = useState<Record<string, TripSlot>>(initialDraft?.slots ?? {});
  /**
   * Everything about the trip that is not its plans, in one place.
   *
   * Two callers now write a trip — the arrangement step saves one so it has somewhere to live, and
   * generating plans creates or updates the same one. Built twice, they would drift, and the drift would be
   * a trip whose saved arrangement did not match the selection it was made from.
   */
  /**
   * Write the trip, whether or not the one it was attached to still exists.
   *
   * `updateTrip` on a trip that is gone answers 404, and until this existed that was a dead end: the screen
   * showed the raw "Không tìm thấy." and the only way forward was to reset and retype everything. It is
   * reachable in ordinary use — the delete button is on the trip list, and a browser holding an id in its
   * draft does not know the trip was deleted from another tab or another device.
   *
   * So a 404 here means "the reference is dead", not "your work is invalid", and the recovery is to make a
   * new trip from the same work. Any other failure is a real failure and is raised.
   */
  const persistTrip = async (body: Record<string, unknown>): Promise<TripResponse> => {
    if (arrangedTripId !== null) {
      try {
        return await client.updateTrip({ id: arrangedTripId }, body);
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 404) throw error;
        // Drop the dead id, or the next save repeats the same 404.
        setArrangedTripId(null);
      }
    }
    return await client.createTrip(body as Parameters<typeof client.createTrip>[0]);
  };

  const tripInput = (): Record<string, unknown> => ({
    // The area's own name, so the trip's destination and its candidates cannot describe different places.
    title: `Chuyến đi ${destinationName}`,
    destination: destinationName,
    start_date: startDate,
    end_date: endDate,
    adults,
    children,
    transport,
    // Sent whenever there are words, whatever chip is lit: a note attached to a named mode is still the
    // user's own description, and dropping it because of which chip is on is how a saved note disappears.
    transport_note: transportNote.trim() !== "" ? transportNote.trim() : null,
    stay_last_night: stayLastNight,
    entry_place_id: entryPlaceId,
    nights: nightDates.map((date, nightIndex) => ({ night_date: date, place_id: lodgingOfNight(nightIndex) })),
    place_ids: Array.from(selectedPlaceIds),
    rest_days: restDays,
    place_days: Object.entries(arrangement).flatMap(([day, ids]) =>
      ids.map((placeId) => ({
        place_id: placeId,
        day_index: Number(day),
        // Absent rather than null when unsaid, so "not chosen" and "chose cả ngày" stay distinguishable.
        ...(slots[`${day}|${placeId}`] !== undefined ? { time_slot: slots[`${day}|${placeId}`] } : {}),
      })),
    ),
  });
  /**
   * Which day is being arranged.
   *
   * One day at a time, and that is the interface rather than a detail. A grid of places by days asks the
   * user to read a matrix — "is this café on day two?" means finding a row and a column and reading a cell
   * — so the first version was reported as hard to picture. Choosing a day and then its places asks the
   * question they actually have: "what is on day two?"
   */
  const [arrangeDay, setArrangeDay] = useState(1);
  const selectArrangeDay = useCallback((day: number): void => {
    setArrangeDay(day);
    /*
     * The rail is reconciled now, and the day in the frame after.
     *
     * The rail first, because a phone has two scroll ports and a day selected while its tab is outside the
     * scroller stays selected and invisible. Every tab is already rendered — only the day's own block changes
     * — so the selected tab can be brought inside the rail without waiting for a render, which also means a
     * background tab, where `requestAnimationFrame` is throttled, still gets the rail right.
     *
     * Measured at 390x844 with the rail at its end — where a drag to reach the third day leaves it — before
     * this: selecting "Ngày 1" put its tab at x -51…80 against a rail of 69…321, 89 of its 131px outside the
     * scroller, its label and count unreadable, with no scrollbar to say the rail had moved (the kit hides
     * it). The same from the other side when › is used to reach a day past the rail's right edge. `nearest`
     * in both axes: the tab is already inside the viewport vertically, so this moves the rail, not the page.
     */
    document.getElementById(`arrange-tab-${day}`)?.scrollIntoView({ block: "nearest", inline: "nearest" });
    requestAnimationFrame(() => {
      const dayEl = document.getElementById(`arrange-day-${day}`);
      if (dayEl) {
        /*
         * `block: "start"`, not `"nearest"`.
         *
         * The day's block is taller than a phone's scrollport, and `nearest` does nothing to an element that
         * is already partly visible — so tapping a day left the day's own heading above the viewport, behind
         * the sticky head and rail. That is the "Ngày 1" the last fix was reported to have missed. `start`
         * honours the block's own `scroll-margin-top`, which is the measured chrome height, so the heading
         * lands just under the rail.
         */
        dayEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }, []);
  /**
   * The id of the trip the arrangement belongs to, which is only known once it has been saved.
   *
   * Three sources, in the order they can be trusted. The session draft is the live one, because it is written
   * as the user works. `localStorage` is what survives a closed browser or a relaunched app: the trip is
   * permanent and the session is not, so a fresh visit used to save a second trip for the same afternoon —
   * measured, the same session reused `75c9ce51…` three times over and a new one created `5acdeed9…`, and the
   * database had twenty-five trips with several the same title on the same dates minutes apart. Not for an
   * edit, whose trip is the route's and must never become what a later new trip appends itself to.
   */
  const [arrangedTripId, setArrangedTripId] = useState<string | null>(
    initialDraft?.arrangedTripId ??
      (isEditing ? null : readTripAnchor(Date.now(), typeof window === "undefined" ? null : window.localStorage)),
  );

  /** Where day 1 starts: the bus station, the airport. Not a visit. */
  const [entryPlaceId, setEntryPlaceId] = useState<string | null>(initialDraft?.entryPlaceId ?? null);

  /** The chosen area's own name, read from the vocabulary rather than from the URL. */
  const destinationName = areas.find((area) => area.id === destinationAreaId)?.name ?? "";

  /**
   * The trip's own title, which is the fact the reset confirm names.
   *
   * The same string `tripInput` writes, so the dialog names the trip by exactly the name the user will see on
   * the trips list — a dialog that said "the draft" would be asking them to recognise work the app has not
   * yet named. Empty before an area is chosen, which the copy has a second sentence for.
   */
  const tripTitle = isEditing
    ? editingQuery.data?.trip.title ?? ""
    : destinationName === ""
      ? ""
      : `Chuyến đi ${destinationName}`;

  /**
   * The nights of the trip, as dates.
   *
   * Derived on the client for *display* and for the assignment list, and enumerated by the server for the
   * response — the same rule written twice, which is a thing to justify rather than to hide. The client
   * needs it before any request exists (step 1 has no trip yet), and the server needs it because it owns
   * the response. They agree because `tripNightDates` in the API is the same arithmetic; this is the one
   * place where "one rule, one home" cannot hold without a round trip for a label.
   */
  /**
   * Every saved place, for the departure select.
   *
   * Not `rawPlaces`: the candidates are the destination area's places, and the bus station nobody filed
   * under that area is exactly the place this field is for. `allPlacesQuery` is the un-narrowed list the
   * screen already holds, so this costs no request.
   */
  const otherPlaces = (allPlacesQuery.data?.items ?? [])
    .map((place) => ({
      id: place.id,
      name: place.name,
      lat: place.location === null ? null : place.location.lat,
      lng: place.location === null ? null : place.location.lon,
      /*
       * Carried so the select can be grouped.
       *
       * The list this control offers is every place the user has ever saved, which is the point — the bus
       * station is filed outside the trip's area — and it also means a lodging arrives sorted in among forty
       * restaurants. Which ones are places to sleep is a category question, and the answer was already in
       * hand.
       */
      isStay: place.categories.includes("stay"),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, "vi"));

  /**
   * The places the user can arrange: everything ticked **except the lodgings**.
   *
   * A stay is a base, not a stop — the engine excludes every `stay` from a day's candidates — so listing one
   * here produced a control that did nothing: tick a homestay onto day 2 and the plan was unchanged, because
   * the day's base comes from the nights, not from this. A control that silently does nothing is the defect
   * this project keeps finding, and it was this screen's own first version.
   *
   * The nights are where a lodging is decided, and they are per night rather than per place, which is what a
   * lodging actually is. What is left here is a read-only statement of what they say.
   */
  const selectedPlaces = rawPlaces.filter(
    (place) => selectedPlaceIds.has(place.id) && !place.categories.includes("stay"),
  );

  /** How many ticked places the arrangement does not mention, which is what a plan will leave out. */
  const unplacedCount = selectedPlaces.filter(
    (place) => !Object.values(arrangement).some((ids) => ids.includes(place.id)),
  ).length;

  const dayCount = useMemo(() => {
    const start = Date.parse(`${startDate}T00:00:00Z`);
    const end = Date.parse(`${endDate}T00:00:00Z`);
    return Number.isNaN(start) || Number.isNaN(end) || end < start
      ? 1
      : Math.round((end - start) / 86_400_000) + 1;
  }, [startDate, endDate]);

  /**
   * The day being arranged, clamped to the trip.
   *
   * Derived rather than stored, because the day count changes when the dates do: shortening a trip from five
   * days to two while day five is open would otherwise leave the step asking a question about a day that no
   * longer exists, and ticking a place there would file it where nothing renders it.
   */
  const activeArrangeDay = Math.min(arrangeDay, Math.max(1, dayCount));

  /**
   * What a place says about when it suits, looked up from the full place list.
   *
   * `rawPlaces` cannot carry it: `PlannerPlace` is the id, the name, the coordinate and the categories —
   * `best_time` is not in it, so the field would offer every slot. `allPlacesQuery` is the list this
   * screen already holds and it has the field, so the lookup costs no request.
   */
  const bestTimeOf = (placeId: string): string[] => {
    const found = (allPlacesQuery.data?.items ?? []).find((place) => place.id === placeId);
    return found?.best_time ?? [];
  };

  /**
   * The days of the trip with nothing arranged on them.
   *
   * Derived from the arrangement and the day count together, so it follows both — shortening a trip from five
   * days to two has to stop reporting days four and five as empty.
   */
  const emptyDays = Array.from({ length: dayCount }, (_, index) => index + 1).filter(
    (day) => (arrangement[day] ?? []).length === 0,
  );

  /** Whether anything has been placed at all — the bulk split is offered only while nothing is. */
  const hasArrangement = Object.values(arrangement).some((ids) => ids.length > 0);

  /** The open day's places, in the order the user arranged them. */
  const dayStopIds = useMemo(() => arrangement[activeArrangeDay] ?? [], [arrangement, activeArrangeDay]);

  /** A place's name by id, for the one line that names where a day starts. */
  function placeNameOf(id: string | null): string {
    return id === null ? "" : savedPlaces.find((place) => place.id === id)?.name ?? "";
  }

  /**
   * Every place the arrangement could name, by id.
   *
   * `rawPlaces` first and `savedPlaces` under it: a day may name a place that has left the destination area,
   * and a day that drew a row for it and then could not say its name would be the screen forgetting what the
   * trip remembers.
   */
  const placeById = useMemo(() => {
    const found = new Map<string, { id: string; name: string; lat: number | null; lng: number | null }>();
    for (const place of savedPlaces) {
      found.set(place.id, {
        id: place.id,
        name: place.name,
        lat: place.location?.lat ?? null,
        lng: place.location?.lon ?? null,
      });
    }
    for (const place of rawPlaces) found.set(place.id, place);
    return found;
  }, [savedPlaces, rawPlaces]);

  /*
   * The departure list, in the two parts a person looks for: the beds this trip uses, and everything else.
   *
   * Only the *chosen* lodgings, not every lodging saved: the difference is a homestay the user did not put
   * into the plan, and offering it as a starting point let it be picked — after which the map drew day 1
   * beginning at a hotel nobody is staying in. A place the trip is not sleeping in cannot be where a day
   * starts, so it is not offered.
   */
  const entryStays = otherPlaces.filter((place) => place.isStay && lodgingChoices.includes(place.id));
  const entryRest = otherPlaces.filter((place) => !place.isStay);

  /**
   * How far a place is from where the day starts, or null when either end has no coordinate.
   *
   * Straight-line and not driven: the driven distance between the day's start and every place the user has
   * not chosen yet is one routed request per row. The number is only used to decide what to look at next, and
   * a straight line is honest about what it is — the row prefixes it with `~` and the step's own disclosure
   * says "đường chim bay".
   */
  function straightLineFromDayStart(place: { lat: number | null; lng: number | null }): number | null {
    if (dayStart === null || place.lat === null || place.lng === null) return null;
    // A tuple rather than an object: `haversineMetres` takes `LatLon`, which core defines as `[lat, lon]`.
    return haversineMetres([dayStart.lat, dayStart.lon], [place.lat, place.lng]);
  }

  const nightDates = useMemo(() => {
    const start = Date.parse(`${startDate}T00:00:00Z`);
    const end = Date.parse(`${endDate}T00:00:00Z`);
    const days = Number.isNaN(start) || Number.isNaN(end) || end < start ? 1 : Math.round((end - start) / 86_400_000) + 1;
    const nights = stayLastNight ? days : days - 1;
    if (Number.isNaN(start) || nights <= 0) return [] as string[];
    return Array.from({ length: nights }, (_, index) =>
      new Date(start + index * 86_400_000).toISOString().slice(0, 10),
    );
  }, [startDate, endDate, stayLastNight]);

  /*
   * The day the board is showing, assembled by the engine's own rule.
   *
   * Which places a day has — the anchor it starts from, the midday return, the night it ends at — is
   * `assembleDay` from `@trippool/core`, the function the planner runs. This screen calls it with the same
   * inputs the API's own handler builds from `trip_nights`: the previous night's lodging or the entry point,
   * the night that ends the day, and whether the party drives home at noon. One rule, so the day drawn here
   * and the day a plan generates cannot disagree about where it begins, when it comes home, or which stop is
   * last.
   *
   * The order of the visits is the one thing this screen does not take from the engine, and `arrangeDay.ts`
   * holds the argument: `assembleDay` sorts a timed day into slot order, which is right for a plan and wrong
   * for a list whose order is the user's own drag.
   */
  /** The lodgings in play, so a choice that left the destination stops being offered. */
  const chosenStays = rawPlaces.filter(
    (place) => place.categories.includes("stay") && lodgingChoices.includes(place.id),
  );

  /**
   * Where a given night is spent.
   *
   * One rule, because two things ask: the trip that gets stored, and the map that is drawn from it. The
   * single-lodging case is why it is a function rather than a lookup. The screen has always *said* "ở X cả
   * n đêm" when one lodging is chosen — there is nothing to assign, so the party sleeps there every night —
   * but the per-night selects only exist from two lodgings up, so `nightLodgings` stayed empty and the saved
   * trip carried `place_id: null` for every night. The plan then had no base to start and end a day at, which
   * is the anchor work undone by the most ordinary trip there is: one hotel. Saying it and storing it are the
   * same claim now.
   */
  const lodgingOfNight = useCallback(
    (nightIndex: number): string | null => {
      const date = nightDates[nightIndex];
      if (date === undefined) return null;
      const chosen = nightLodgings[date];
      if (chosen !== undefined) return chosen;
      return chosenStays.length === 1 ? chosenStays[0]!.id : null;
    },
    [nightDates, nightLodgings, chosenStays],
  );

  const arrangeBoard = useMemo(() => {
    // Every place the step could name, by id — an arrangement outlives the places it mentions, and a place
    // deleted since it was placed there has to be counted rather than silently missing from the map.
    const known = new Map<string, ArrangePlace>();
    for (const place of savedPlaces) {
      known.set(place.id, {
        id: place.id,
        name: place.name,
        location: place.location === null ? null : { lat: place.location.lat, lon: place.location.lon },
      });
    }
    const day = activeArrangeDay;
    /*
     * Day 1 starts at the trip's entry point rather than at a previous night, and every later day starts
     * where the party woke up. That is the same pairing the server builds: night `d - 1` ends day `d`, and
     * night `d` is where it sleeps.
     */
    const fromId = day === 1 ? entryPlaceId ?? null : lodgingOfNight(day - 2);
    return assembleArrangeDay({
      visits: (arrangement[day] ?? []).map((placeId) => {
        const slot = slots[`${day}|${placeId}`];
        return slot === undefined ? { placeId } : { placeId, slot };
      }),
      from: fromId === null ? null : { placeId: fromId, kind: day === 1 ? "entry" : "lodging" },
      to: lodgingOfNight(day - 1),
      returnToLodging: restDays.includes(day),
      known,
    });
  }, [
    savedPlaces,
    arrangement,
    slots,
    restDays,
    activeArrangeDay,
    entryPlaceId,
    lodgingOfNight,
  ]);

  /**
   * Where a day starts: the entry point on day 1, and the previous night's bed after that.
   *
   * One rule with three callers — the open day's own column, the distance on each day's tab, and the anchors
   * the bulk distribution is given. These are the same question, and the screen had two answers to it: the
   * assembled day read the rule out of its own inputs, and the distribution kept a second copy.
   */
  const anchorOfDay = useCallback(
    (day: number): { lat: number; lon: number } | null => {
      const id = day === 1 ? entryPlaceId : lodgingOfNight(day - 2);
      if (id === null) return null;
      const place = savedPlaces.find((candidate) => candidate.id === id);
      return place?.location == null ? null : { lat: place.location.lat, lon: place.location.lon };
    },
    [entryPlaceId, savedPlaces, lodgingOfNight],
  );

  /**
   * Where the open day begins, so a row can say how far off it is.
   *
   * The assembled day's first stop is the day's anchor when it has one — `assembleDay` puts the departure or
   * the previous night first — and its first visit otherwise. When neither exists the day has no start to
   * measure from, and no row claims a distance.
   */
  const dayStart = useMemo((): { lat: number; lon: number } | null => {
    const first = arrangeBoard.stops[0];
    if (first !== undefined) return { lat: first.lat, lon: first.lon };
    return anchorOfDay(activeArrangeDay);
  }, [arrangeBoard.stops, activeArrangeDay, anchorOfDay]);

  /**
   * A day's length in a straight line, for the tab that offers it.
   *
   * Measured here rather than routed: asking the provider for every day at once is one request per day for a
   * number whose whole job is to say which days are heavy and which are empty — the driven total is on the
   * map when that day is open. Straight-line, so the tab prefixes it with `~`, exactly as the store's rows do.
   */
  function dayStraightLineMetres(day: number): number | null {
    const points: Array<{ lat: number; lon: number }> = [];
    const anchor = anchorOfDay(day);
    if (anchor !== null) points.push(anchor);
    for (const id of arrangement[day] ?? []) {
      const place = placeById.get(id);
      if (place !== undefined && place.lat !== null && place.lng !== null) {
        points.push({ lat: place.lat, lon: place.lng });
      }
    }
    // One point is not a distance, and a day with nothing on it has no length to report.
    if (points.length < 2) return null;
    let metres = 0;
    for (let index = 1; index < points.length; index += 1) {
      const from = points[index - 1]!;
      const to = points[index]!;
      metres += haversineMetres([from.lat, from.lon], [to.lat, to.lon]);
    }
    return metres;
  }

  /*
   * The day's points for the router: the same sequence the map draws and the list renders.
   *
   * The order comes from `arrangeBoard.stops` and nowhere else — no `order` field and no provider index is
   * applied to it anywhere below, which is the point. `arrangeDay.ts` has already put the visits in the order
   * the user arranged them, so what is sent is an instruction and not a question, and the numbers on the pins
   * are positions in this very array.
   */
  const arrangePoints = useMemo(
    () => arrangeBoard.stops.map((stop) => ({ lat: stop.lat, lon: stop.lon })),
    [arrangeBoard.stops],
  );
  /*
   * The road is drawn **in the order the user arranged**, always.
   *
   * It was requested with `fixed: arrangeBoard.fixed`, so a day with no times on it came back in the
   * optimiser's own order: the map drew a road through the stops in an order nobody had asked for, the pins
   * were numbered by that order rather than by the list's, and dragging a stop changed nothing on the map.
   * Reported as "the map does not follow when I reorder" — and it did not, because the request was answering
   * a different question from the one the screen is asking.
   *
   * A day whose stops carry times was the other half of the same complaint, and `fixed` alone could not fix
   * it: the points were already sorted by `assembleDay` before this request was built, so asking the provider
   * to keep an order it was never given changed nothing either. `arrangeDay.ts` no longer hands it that order,
   * and `true` here says the same thing about the road that the screen says about the list — drive this.
   *
   * What this step shows is the day being built. Its order is the instruction, not a suggestion, and the map
   * is the picture of that instruction: the same numbers in the same sequence, moving when the list moves —
   * and the plan now drives that instruction rather than replacing it. An arranged day is routed in its own
   * order, whether or not it carries times, so the list, the map and the itinerary that comes later are one
   * sequence; the shortest order is only ever the one the panel under the map offers to write into the
   * arrangement. The exception is the day the user *timed*: slot order is the sequence they stated there,
   * `assembleDay` assembles it that way, and the panel therefore offers no reorder for such a day — see
   * `arrangeShortestRoad`.
   */
  const arrangeRoad = useRoutePreview(userId, arrangePoints, true, step === 2);
  /*
   * The pins, numbered by the list's own order — and not by anything the provider answers.
   *
   * This used to re-order the stops by `arrangeRoad.data.order`, which was right while the road was requested
   * as "find the shortest one": the pins then followed the road that was actually drawn. The request is now
   * "drive it as arranged", and that makes the provider's `order` a different thing to agree with — measured
   * on a six-stop day, the list's first stop and the pin numbered 1 were two different places, because the
   * numbering came from the provider's tour while the list came from the user's arrangement.
   *
   * So the pins are built straight from the assembled day, in its order, and the two agree because the
   * assembled day *is* the arrangement's order — the visits were re-seated into it in `arrangeDay.ts`, since
   * the sort `assembleDay` applies is a rule for a plan and not for this preview. Nothing subordinate to a
   * provider answer is left here to disagree with.
   */
  const arrangeMapStops: RouteMapStop[] = useMemo(
    () =>
      stopMarkers(arrangeBoard.stops).map((marker, index) => {
        const stop = arrangeBoard.stops[index]!;
        /*
         * The name the row shows, not the name the database holds.
         *
         * The popup opened from a pin and the row beside it are the same place, and they were printing
         * different text for it — `chuyencuanang5` on the map, `Chuyencuanang 5` in the list — because the
         * stop's name arrives as stored and the row formats it. One place, one name.
         */
        return { ...stop, name: displayName(stop.name), marker, position: index };
      }),
    [arrangeBoard.stops],
  );

  /*
   * The legs as the map wants them, memoised for the map's own sake: `RouteMap` rebuilds its overlay when its
   * props change by identity, so an array built inside this render would redraw the day on every keystroke
   * elsewhere on the screen.
   *
   * `geometry` travels with each leg, which is what the map paints a section of the day from — each drive in
   * its own colour. It is `null` for a leg the provider gave no path for, and the map then skips that leg's
   * line (its pins and its distance still show) rather than connecting its ends with a straight line.
   */
  const arrangeMapLegs: RouteMapLeg[] = useMemo(
    () => (arrangeRoad.data?.legs ?? []).map((leg) => ({ distanceM: leg.distance_m, geometry: leg.geometry })),
    [arrangeRoad.data],
  );

  /**
   * The drive that reaches each row, or null when the provider priced none for it.
   *
   * By **destination**, not by the pair of rows. The drive a row is about is the leg that arrives at its
   * stop, and the stop before it is usually the row above — but not always: on a rest day the midday return
   * sits between two rows. Measured on a four-stop day whose return lands before the last stop, the road runs
   * `… → 3 → ☕ → 4 → 🏨`, and a lookup keyed on the pair of *rows* found no drive for row 4 at all while the
   * two above it had theirs — the number was in the response, priced against a stop that is not a row.
   *
   * So the rows are matched to the legs by walking the day's points in order. `arrangeBoard.stops` carries
   * the visits in the arrangement's own order — the order this list renders, which `arrangeDay.ts`
   * establishes — and the day was sent in that order, so leg `i` is the drive from stop `i` to stop `i + 1`
   * and the leg *into* a stop is the one before it. The walk counts visits as it goes, so the k-th visit is
   * row k.
   *
   * Two legs are deliberately left off. The one into the night's lodging arrives at an anchor and never at a
   * row, so no row can claim it. And a row whose stop is not on the map at all — no coordinate, or deleted
   * since it was arranged — is given nothing, which the id check is for: without it the walk would hand that
   * row the drive into the next place that *is* on the map, a real number for a drive nobody makes.
   */
  const legIntoRow = useMemo(() => {
    const legs = arrangeRoad.data?.legs ?? [];
    const byRow: Array<{ distanceM: number | null; durationS: number | null } | null> = [];
    let row = 0;
    arrangeBoard.stops.forEach((stop, index) => {
      if (stop.kind !== "visit") return;
      // Nothing arrives at the day's first point, so a row that is the day's own beginning says nothing.
      const leg = index === 0 ? undefined : legs[index - 1];
      byRow[row] =
        dayStopIds[row] === stop.id && leg !== undefined
          ? { distanceM: leg.distance_m, durationS: leg.duration_s }
          : null;
      row += 1;
    });
    return byRow;
  }, [arrangeBoard.stops, arrangeRoad.data, dayStopIds]);

  /**
   * What the line under the map says, and what it deliberately does not.
   *
   * `RouteMap` already states an unmeasured road, in its own words and in the same place; measured side by side,
   * a second sentence about the provider here was the same fact twice. So this carries what the map cannot
   * know: the day's total when there is one, whether the road is still on its way, and the two absences that
   * would otherwise show only as a shorter day — stops with no coordinate, and stops deleted since they were
   * arranged. It is empty when it has nothing of its own to add.
   *
   * The total is the drive the line above it draws, which is the arrangement's order — see `arrangeRoad`. On
   * a day whose stops carry times that is deliberately *not* the drive the plan will make: the engine sorts
   * such a day into slot order when it generates, and the rules behind the info control say so in the user's
   * own language. One sequence was the decision, so the pins, the line and this number are that sequence; a
   * total measured over the slots while the line followed the drag would be a second order wearing one map.
   */
  const arrangeRoadNote = ((): string => {
    const missing: string[] = [];
    if (arrangeBoard.unlocatable > 0) {
      missing.push(`${arrangeBoard.unlocatable} chỗ chưa có toạ độ nên không lên bản đồ.`);
    }
    if (arrangeBoard.missing > 0) {
      missing.push(`${arrangeBoard.missing} chỗ đã bị xoá khỏi danh sách đã lưu.`);
    }
    const suffix = missing.length === 0 ? "" : ` ${missing.join(" ")}`;

    /*
     * The two absences, branched on the **visits** first and the stops second, because the day's point list
     * is not the day's places.
     *
     * `arrangeBoard.stops` carries the day's anchors as well as its visits — the night's lodging, the trip's
     * entry point — so a day with nothing on it but a bed has one stop and no visit. Measured with the
     * arrangement untouched: the column beside the map said "Ngày này chưa có chỗ nào" (it branches on the
     * day's own place list) while this line said "Một chỗ thì chưa có đường đi", two descriptions of one day
     * on one screen. The visit count is what the column counts, so the no-place sentence is branched on
     * that, and the day is described the same way in both places.
     *
     * The no-road sentence stays on the stop count, because the road is drawn between the day's *points*:
     * one point is no road whatever kind it is, and a visit with an entry point beside it is a real leg the
     * map draws and prices — which the visit count alone would have suppressed.
     */
    const visits = arrangeBoard.stops.filter((stop) => stop.kind === "visit").length;
    if (visits === 0) return `Ngày ${activeArrangeDay} chưa có chỗ nào.${suffix}`;
    if (arrangeBoard.stops.length === 1) return `Một chỗ thì chưa có đường đi.${suffix}`;
    if (arrangeRoad.data === undefined) return `Đang tính đường cho ngày ${activeArrangeDay}…${suffix}`;
    // The map says the road could not be measured; nothing to add but the counts.
    if (!arrangeRoad.data.distances_known) return missing.join(" ");
    /*
     * `đường đi` on this number, and not on the tab's.
     *
     * The two sit on one screen and they disagree by design: the tab sums straight lines between the stops and
     * this is the road the provider drove between them — measured for one Đà Lạt day, `~23.3 km` against
     * `45.9 km`. A reviewer read the pair as a contradiction, which is what an unlabelled number in a comparison
     * always is. The tab carries a `~` and the rules behind the info control say what it measures; this one says
     * so in the sentence it was already in.
     */
    return (
      `Ngày ${activeArrangeDay}: ${formatKm(arrangeRoad.data.distance_m)} đường đi · ` +
      `${formatDrivingMinutes(arrangeRoad.data.driving_min)} lái xe.${suffix}`
    );
  })();

  /**
   * The same day as the optimiser would drive it, which is the alternative the panel offers.
   *
   * `false` is the whole difference from `arrangeRoad` above: this asks the provider to choose the order,
   * where that one asks it to drive the arrangement. Against it `arrangeRoad.data` measures the side the user
   * is looking at, which is what makes the two numbers the panel prints a comparison of orders rather than
   * two unrelated drives.
   *
   * Asked for only when the day states no times. A day whose every stop carries one is driven by its slots at
   * generation time, so a reorder could never be taken — the arrangement would be sorted straight back — and
   * the offer would sit there one tap from doing nothing (`arrangeOrder.ts` argues it at length). Two stops
   * are one order, so there is nothing to compare and no call to spend; and once both answers are in,
   * `suggestOrder` stays quiet unless the optimiser really saves a metre.
   */
  const arrangeShortestRoad = useRoutePreview(
    userId,
    arrangePoints,
    false,
    step === 2 && !arrangeBoard.fixed && arrangeBoard.stops.length >= 3,
  );

  /**
   * The suggestion, when there is one.
   *
   * `shortest` is the side the router ordered itself and `asArranged` is the side the user's own order
   * produced — whichever way round the primary answer happens to hold them. A saving of zero is not a
   * suggestion: the smallest that is worth a sentence is a real metre.
   */
  const arrangeSuggestion = suggestOrder({
    shortest: arrangeShortestRoad.data,
    asArranged: arrangeRoad.data,
  });

  /**
   * The way back from the suggestion, because the owner asked for it.
   *
   * The owner wanted to compare the two orders — take the shortest one, see the day priced at it, and put the
   * arrangement back the way they had it — so taking the suggestion now leaves the order it replaced behind
   * and the same box above the map offers the one tap that restores it. `arrangeOrder.ts` owns the record and
   * the rule about when it stops being valid; this is where it is held and drawn.
   *
   * One step, on one day. Taking a suggestion on another day replaces it rather than growing a history, which
   * is what "a single step of undo" means and what keeps the arrangement's own state the only thing here.
   */
  const [suggestionUndo, setSuggestionUndo] = useState<SuggestionUndo | null>(null);

  /*
   * The undo goes the moment the day it belongs to is no longer the day the suggestion made.
   *
   * An effect on the arrangement rather than a call inside each of the five places that change it (a drag, the
   * keyboard move, the tick, the bulk split, the reset): the one that is forgotten is the one that leaves a
   * button restoring an order onto stops the user has since changed, and a state derivation cannot be
   * forgotten. `standingUndo` returns the same record while it still holds, so this settles after one
   * re-render and stops.
   */
  useEffect(() => {
    setSuggestionUndo((previous) => (previous === null ? null : standingUndo(previous, arrangement)));
  }, [arrangement]);

  /*
   * Dragging, in the two directions this step needs: a stop within its own day, and a place from the store
   * onto a day.
   *
   * Pointer events rather than the HTML drag-and-drop API, which does not fire for a touch at all — and a
   * phone is where this screen is used. A drag begins on the first few pixels of travel rather than on the
   * press, because the grip is also a control a keyboard user focuses and a finger taps.
   */
  const [drag, setDrag] = useState<{ kind: "stop" | "store"; placeId: string } | null>(null);
  /** Where the dragged stop would land, as an insertion index between the day's rows. */
  const [dropAt, setDropAt] = useState<number | null>(null);
  /** Which day a dragged store row is over, so the tab it would land on can say so. */
  const [dropDay, setDropDay] = useState<number | null>(null);
  /** Whether the store's slide-over is showing. */
  const [storeOpen, setStoreOpen] = useState(false);
  /**
   * Whether the reset confirm is open.
   *
   * A flag rather than a `target` object, unlike the delete confirms elsewhere: those name a row that can be
   * removed while the dialog is up, so the target has to be held. This one has a single subject — the draft
   * being edited, which is the screen's own state and cannot go stale.
   */
  const [resetOpen, setResetOpen] = useState(false);
  /** Leaving the planner goes back to where it was opened from, or to the trip list when it was opened alone. */
  const goBack = useGoBack("/trips");
  const storeDrawerRef = useRef<HTMLDivElement>(null);
  const stopRows = useRef<Array<HTMLLIElement | null>>([]);
  const dragOrigin = useRef({ x: 0, y: 0 });
  const dragStarted = useRef(false);
  /** True for exactly one click after a drag, so letting go of a store row does not also tick its box. */
  const afterDrag = useRef(false);
  /*
   * Where the drag would currently land, mirrored out of the state.
   *
   * The move handler runs off the window rather than off a re-rendered element, so it cannot read this render's
   * `dropAt` — it would be frozen at the value the gesture started with, and every drop would land where the
   * pointer was when the finger went down.
   */
  const dropAtRef = useRef<number | null>(null);
  const dropDayRef = useRef<number | null>(null);

  /*
   * The sticky chrome's heights, published for the sticky elements below them.
   *
   * `.planner-screen__head` is sticky and opaque, and the day rail (`.daytabs`) and the arrange map
   * (`.arrange__road`) are sticky in the same scroll container — all at the top, where each paints over the
   * one before it. What they need is the height of everything above them as their sticky offset, and that
   * is not a constant: the head measures one height at 390px and another at 1024px, a step whose title wraps
   * changes it again, and the rail wraps too. They are measured here rather than written into the stylesheet
   * so the sheet cannot hold a stale copy of a layout fact, and published as `--planner-head-h` and
   * `--planner-daytabs-h` on the screen so the CSS can offset from them.
   *
   * Measured when the head attaches (the callback ref, before paint) and again whenever the viewport, the
   * step or the rail's own box changes — all the things that move these heights. The rail's box is watched
   * rather than recomputed from those events because its height moves on its own (see `measureRail`); the
   * resize listener is kept as well, because a value a scroll position is measured against wants the
   * un-throttled signal and not only the observer's rendering opportunity.
   */
  const headRef = useRef<HTMLDivElement | null>(null);

  const measureHead = useCallback((head: HTMLDivElement | null): void => {
    headRef.current = head;
    publishStickyHeights(head);
  }, []);

  /*
   * The day rail's own attach, because it is not in the first commit.
   *
   * The head is on every step, but the rail is step 2's and only renders once the places have loaded — so a
   * measurement taken when the head attaches finds no rail and publishes zero, and nothing would publish
   * again when it appears. This ref republishes the moment the rail attaches, and then watches the rail for
   * the rest of its life.
   *
   * The watch is the half that was missing. The rail's height is not fixed once it is up: the bulk-split
   * button is the rail's first line and exists only while nothing is arranged, so the same element measures
   * 117px before that tap and 59px after it (and the reverse when the last place is taken off a day) — and
   * neither the step change nor a resize republishes for that. The stale value is read as an offset, so the
   * map pane at >=1024px and every day's `scroll-margin-top` were set from a rail height that no longer
   * existed. An observer on the rail itself is the one signal that cannot go stale.
   */
  const railObserver = useRef<ResizeObserver | null>(null);

  const measureRail = useCallback((rail: HTMLDivElement | null): void => {
    publishStickyHeights(headRef.current);
    railObserver.current?.disconnect();
    railObserver.current = null;
    if (rail === null) return;
    const observer = new ResizeObserver(() => publishStickyHeights(headRef.current));
    observer.observe(rail);
    railObserver.current = observer;
  }, []);

  /*
   * The pinned bar's own attach and watch.
   *
   * It renders on steps 1-3 but its height is not fixed once it is up: the "Còn N ngày trống" line sits on a
   * row of its own and appears or leaves as the last place is taken off a day or put back, which takes the
   * bar from 77px to 109px (measured at 390px). Nothing about that reaches `publishStickyHeights` — it is not
   * a resize and not a step change — and a stale value here would leave the day's last card inside the bar's
   * strip. Observed on the element itself, the way the rail is, for the same reason.
   */
  const savebarObserver = useRef<ResizeObserver | null>(null);

  const measureSavebar = useCallback((bar: HTMLDivElement | null): void => {
    publishStickyHeights(headRef.current);
    savebarObserver.current?.disconnect();
    savebarObserver.current = null;
    if (bar === null) return;
    const observer = new ResizeObserver(() => publishStickyHeights(headRef.current));
    observer.observe(bar);
    savebarObserver.current = observer;
  }, []);

  useLayoutEffect(() => {
    const publish = (): void => publishStickyHeights(headRef.current);
    publish();
    window.addEventListener("resize", publish);
    return () => window.removeEventListener("resize", publish);
  }, [step]);

  function beginDrag(event: ReactPointerEvent<HTMLElement>, target: { kind: "stop" | "store"; placeId: string }): void {
    dragOrigin.current = { x: event.clientX, y: event.clientY };
    dragStarted.current = false;
    setDrag(target);
  }

  /**
   * The move and the release, on the window for as long as the drag lasts.
   *
   * Pointer capture would deliver them to the element the gesture began on, which is the usual way to do this —
   * and it is one `setPointerCapture` failure away from a drag that silently does nothing. Measured on a 390px
   * screen: the store row could be dragged up the page and the drop never registered, because the release
   * happens over a day tab a thousand pixels from the row that was grabbed, and nothing anywhere said so.
   * Listening on the window has neither problem: nothing needs capturing, and the pointer may leave its
   * starting element at any moment — which on a phone it always does.
   */
  useEffect(() => {
    if (drag === null) return;

    const move = (event: PointerEvent): void => {
      if (!dragStarted.current) {
        const travelled =
          Math.abs(event.clientX - dragOrigin.current.x) + Math.abs(event.clientY - dragOrigin.current.y);
        if (travelled < 8) return;
        dragStarted.current = true;
      }

      if (drag.kind === "stop") {
        const rows = stopRows.current.flatMap((row) => {
          if (row === null) return [];
          const box = row.getBoundingClientRect();
          return [{ top: box.top, bottom: box.bottom }];
        });
        const at = dropIndexAt(rows, event.clientY);
        dropAtRef.current = at;
        setDropAt(at);
        return;
      }

      // The day under the pointer, read from the document: the tabs are nowhere near the row that was grabbed.
      const under = document.elementFromPoint(event.clientX, event.clientY);
      const day = under?.closest("[data-day]")?.getAttribute("data-day");
      const at = day === null || day === undefined ? null : Number(day);
      dropDayRef.current = at;
      setDropDay(at);
    };

    const end = (): void => {
      const started = dragStarted.current;
      const at = dropAtRef.current;
      const day = dropDayRef.current;
      setDrag(null);
      setDropAt(null);
      setDropDay(null);
      dropAtRef.current = null;
      dropDayRef.current = null;
      if (!started) return;

      // One click is about to arrive where the pointer let go, and on a row whose whole body is a checkbox
      // label that click would tick the place onto the day it was just dragged away from.
      afterDrag.current = true;

      if (drag.kind === "stop") {
        const from = dayStopIds.indexOf(drag.placeId);
        if (from === -1 || at === null) return;
        /*
         * `dropIndexAt` answers where a row would be *inserted*, and a stop already in the list that is dropped
         * past its own position lands one earlier than the line the user aimed at — the row being moved is still
         * counted among the rows the line was drawn between.
         */
        const to = at > from ? at - 1 : at;
        const next = moveItem(dayStopIds, from, to);
        if (next !== null) setArrangement({ ...arrangement, [activeArrangeDay]: next });
        return;
      }

      if (day === null) return;
      const onThatDay = arrangement[day] ?? [];
      if (!onThatDay.includes(drag.placeId)) {
        setArrangement({ ...arrangement, [day]: [...onThatDay, drag.placeId] });
      }
      setArrangeDay(day);
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
    /*
     * `arrangement` and `dayStopIds` are read when the drag starts and nothing mutates them while one is in
     * flight, so re-subscribing on every drop target would buy nothing and cost a listener per frame. The
     * effect is deliberately keyed on the drag alone.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the drag's own key, by design (see above).
  }, [drag]);

  /**
   * The same move, from the keyboard.
   *
   * A drag is a pointer gesture with no keyboard equivalent, and the order of a day decides its driving
   * total — so the handle takes an arrow key and moves its own row, then takes focus back, because the
   * element the browser is holding focus on has just been re-rendered somewhere else.
   */
  function moveStopByKeyboard(event: ReactKeyboardEvent<HTMLButtonElement>, from: number): void {
    const to = event.key === "ArrowUp" ? from - 1 : event.key === "ArrowDown" ? from + 1 : null;
    if (to === null) return;
    event.preventDefault();
    const next = moveItem(dayStopIds, from, to);
    if (next === null) return;
    setArrangement({ ...arrangement, [activeArrangeDay]: next });
    requestAnimationFrame(() => {
      stopRows.current[to]?.querySelector<HTMLButtonElement>(".arrange__grip")?.focus();
    });
  }

  function toggleRestDay(day: number): void {
    setRestDays((previous) =>
      previous.includes(day) ? previous.filter((candidate) => candidate !== day) : [...previous, day],
    );
  }

  /**
   * Spread the whole selection over the trip's days.
   *
   * The step has always promised this in words — "bỏ trống hết thì hệ thống tự chia ngày" — and the promise
   * was kept by the *engine*, at generation time, on a screen two steps later. Doing it here rather than
   * saving it for the planner is the difference between a default and a starting point: a distribution the
   * user can see, keep, or drag one place out of. Places near each other land on the same day and each day is
   * ordered from its own anchor, which is a heuristic — `autoArrangeDays` says which one and why.
   *
   * Only reachable while the days are empty (the button that calls it is not rendered otherwise), so it
   * never replaces an arrangement: the re-split that did, and dropped arranged entries along the way, is
   * gone rather than confirmed.
   */
  function autoAssignDays(): void {
    setArrangement(
      autoArrangeDays({
        places: selectedPlaces.map((place) => ({ id: place.id, lat: place.lat, lon: place.lng })),
        days: dayCount,
        anchorOf: anchorOfDay,
      }),
    );
    setArrangeDay(1);
  }

  /*
   * Every change to the anchor is a write to storage.
   *
   * An effect rather than a write inside each mutation's `onSuccess`, because the id is dropped in three
   * places — a successful save, a save that found the trip deleted, and "Đặt lại" — and the one that is
   * forgotten is the one that leaves a browser pointing at a trip that is gone. In an edit the trip belongs to
   * the route, so an edit neither writes the anchor nor clears one belonging to a new trip in another tab.
   */
  useEffect(() => {
    if (isEditing || typeof window === "undefined") return;
    writeTripAnchor(arrangedTripId, Date.now(), window.localStorage);
  }, [arrangedTripId, isEditing]);

  /*
   * Escape and clicking outside close the store drawer.
   */
  useEffect(() => {
    if (!storeOpen) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setStoreOpen(false);
    };
    const onPointerDown = (event: PointerEvent): void => {
      if (drag !== null || dragStarted.current || afterDrag.current) return;
      const target = event.target;
      if (target instanceof Node && storeDrawerRef.current?.contains(target) !== true) {
        const element = target instanceof Element ? target : target.parentElement;
        if (element?.closest(".arrange__more")) return;
        setStoreOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [storeOpen, drag]);

  /**
   * Put the shortest order into the arrangement, which is the day's to keep — and keep the order it replaced,
   * so the owner can put it back and compare the two.
   *
   * The record is written in the same update as the arrangement. Two setters, one gesture: a render in which
   * the day has moved and no undo points at the order it moved from is a frame where the button would restore
   * nothing, and writing them from one handler is what makes that unrepresentable.
   */
  function applyShortestOrder(): void {
    if (arrangeSuggestion === null) return;
    const day = activeArrangeDay;
    const before = arrangement[day] ?? [];
    const next = reorderedArrangement({
      order: arrangeSuggestion.order,
      stops: arrangeBoard.stops,
      current: before,
    });
    if (next === null) return;
    setArrangement({ ...arrangement, [day]: next });
    setSuggestionUndo({ day, before, after: next });
  }

  /**
   * Put the arrangement back the way it was before the suggestion was taken.
   *
   * Only reachable while the record still describes the day (`standingUndo` above), so the order restored is
   * the order that day ended the take with, and the places on it are the places it had. Clearing the record
   * in the same update is what brings the suggestion back: the day is the user's own order again, and the
   * saving it was measured against is real again.
   */
  function undoShortestOrder(): void {
    if (suggestionUndo === null) return;
    setArrangement({ ...arrangement, [suggestionUndo.day]: suggestionUndo.before });
    setSuggestionUndo(null);
  }

  /**
   * Fill the form from the stored trip, once.
   *
   * `seeded` and not a `useEffect` on the query alone: the effect would re-run on every refetch and overwrite
   * whatever the user had changed since, which is a form that undoes typing. It runs once per mount, and a
   * reload re-seeds from the server — which is the reason the server is the source for an existing trip and
   * the draft is not preferred over it.
   */
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    const stored = editingQuery.data;
    if (!isEditing || stored === undefined || seeded) return;
    setSeeded(true);

    setDestination(stored.trip.destination);
    setStartDate(stored.trip.start_date);
    setEndDate(stored.trip.end_date);
    setAdults(stored.trip.adults);
    setChildren(stored.trip.children);
    setTransport(stored.trip.transport);
    setTransportNote(stored.trip.transport_note ?? "");
    /*
     * The note is restored on its own, and the chip from the mode.
     *
     * Treating "has a note" as "chose Khác" looked right for a trip this screen made and is wrong for any
     * other: measured, a trip stored with `motorbike` and a note came back with the Khác chip lit and the mode
     * it actually had silently replaced. The note is the user's own words and the chip is which of the five
     * ways they travel — the schema keeps them independent, and only this screen had decided they were one.
     */
    setTransportIsOther(stored.trip.transport === "public_transport" && stored.trip.transport_note !== null);
    setStayLastNight(stored.trip.stay_last_night);
    setEntryPlaceId(stored.trip.entry_place_id);
    /*
     * Only the pace is restored. The other three contract fields (`wake_time`, `check_in_time`,
     * `max_driving_minutes`) are no longer offered by any control, and the plan handler reads them from the
     * trip's own row when it generates — so there is nothing on this screen to fill in from them.
     */
    setPace(stored.constraints.pace);
    /*
     * The destination, matched to a saved area by name.
     *
     * A trip stores a destination *string*; the candidates come from an *area*. They agree because this screen
     * writes the area's own name, and an existing trip whose destination names no area keeps its places by
     * having its own name offered as an option — `areas` below adds it when it is missing.
     */
    setDestinationAreaId(
      (areasQuery.data?.areas ?? []).find((area) => area.name === stored.trip.destination)?.id ?? "",
    );
    setNightLodgings(
      Object.fromEntries(
        stored.nights
          .filter((night) => night.place_id !== null)
          .map((night) => [night.night_date, night.place_id as string]),
      ),
    );

    const byDay: Record<number, string[]> = {};
    const times: Record<string, TripSlot> = {};
    for (const entry of stored.place_days) {
      byDay[entry.day_index] = [...(byDay[entry.day_index] ?? []), entry.place_id];
      if (entry.time_slot !== null && entry.time_slot !== undefined) {
        times[`${entry.day_index}|${entry.place_id}`] = entry.time_slot;
      }
    }
    setArrangement(byDay);
    setSlots(times);
    setRestDays(stored.rest_days);
    // The id the writes target: without it, saving an edit would create a second trip and orphan the first.
    setArrangedTripId(stored.trip.id);
    setStep(1);
    /*
     * The seed runs once, by design: `seeded` is set on the first pass, so listing `areasQuery.data?.areas`
     * would not re-run anything — it would only claim this effect re-seeds when the areas resolve, which the
     * guard above exists to prevent, because re-seeding would overwrite a form the user had begun editing.
     * The areas are read here only to resolve a stored destination name to an area id.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed-once effect, guarded by `seeded`.
  }, [isEditing, editingQuery.data, seeded]);


  /** Every lodging the destination offers, which is what the choice is made from. */
  const staysInDestination = rawPlaces.filter((place) => place.categories.includes("stay"));

  /*
   * A choice that stopped being possible is dropped.
   *
   * The select offers the trip's own lodgings, so taking a lodging out of the plan leaves an entry point
   * pointing at somewhere the party is not staying — picked before, unreal now. Clearing it is the honest
   * response: day 1 then starts at its first stop, which is what a trip with no anchor does everywhere else.
   */
  useEffect(() => {
    if (entryPlaceId === null || lodgingChoices.includes(entryPlaceId)) return;
    const chosen = savedPlaces.find((place) => place.id === entryPlaceId);
    if (chosen === undefined || !chosen.categories.includes("stay")) return;
    setEntryPlaceId(null);
  }, [entryPlaceId, lodgingChoices, savedPlaces]);

  /*
   * One lodging in the destination is the lodging the trip uses.
   *
   * It was a chip the user had to press to say what the data already said, next to a departure select listing
   * the same place — the redundancy a reviewer flagged, and they were right that it is one: with a single bed to
   * choose, the choice is not a choice. Two or more keeps the chips, because then it is, and the per-night rows
   * are the case they exist for.
   *
   * Only for a trip being made: an existing trip's own answer is the server's, and a screen that filled in a
   * blank the user had deliberately left would be editing their trip for them.
   */
  useEffect(() => {
    if (isEditing || nightDates.length === 0) return;
    const only = staysInDestination.length === 1 ? staysInDestination[0] : undefined;
    if (only === undefined || lodgingChoices.length > 0) return;
    setLodgingChoices([only.id]);
  }, [isEditing, staysInDestination, nightDates.length, lodgingChoices.length]);

  /**
   * A chosen lodging the destination no longer offers, dropped — the nights' own reconcile, one level up.
   *
   * `chosenStays` is this choice intersected with the destination, so a stale id leaves it empty *while the
   * choice is non-empty* — and the default below, which picks a stay for the user, only fires while the choice
   * is empty. Measured: planning Đà Lạt with the default hotel and then switching the destination to Vũng Tàu
   * left the step saying "Chưa chọn chỗ ở — lộ trình sẽ không có điểm neo nào cho mỗi ngày" with Vũng Tàu's
   * hotels in the list behind it, because the one id it still held belonged to Đà Lạt.
   */
  useEffect(() => {
    const offered = new Set(staysInDestination.map((place) => place.id));
    setLodgingChoices((previous) => {
      const kept = previous.filter((id) => offered.has(id));
      // The same array back when nothing was dropped, so the effect does not loop on its own work.
      return kept.length === previous.length ? previous : kept;
    });
  }, [staysInDestination]);


  /**
   * One lodging means the whole trip.
   *
   * The default the user asked for: choosing a single place to stay is choosing it for every night, and writing
   * that into the nights is what lets the plan's anchors and the check-in floor work without anyone touching a
   * per-night control. With two or more, nothing is written and the nights are the user's to assign.
   */
  useEffect(() => {
    const only = lodgingChoices.length === 1 ? lodgingChoices[0] : undefined;
    if (only === undefined || nightDates.length === 0) return;
    setNightLodgings((previous) => {
      const next: Record<string, string | undefined> = { ...previous };
      let changed = false;
      for (const date of nightDates) {
        if (next[date] !== only) {
          next[date] = only;
          changed = true;
        }
      }
      // The same object back when it already said this, so the effect does not loop on its own write.
      return changed ? next : previous;
    });
  }, [lodgingChoices, nightDates]);

  /**
   * The arrangement reconciled to the trip's own days, because the chips are the only way to reach a day.
   *
   * Shortening the dates from five days to two leaves days three to five in the arrangement, and every screen
   * that renders a day renders `1..dayCount` — so a place sitting on day 5 is unreachable: no chip to open it,
   * nothing to untick. Worse, the count under the list scanned *every* day and so certified those places as
   * placed, while the engine drops them at generation time and reports "địa điểm được xếp vào ngày vượt quá số
   * ngày của chuyến". Dropping them here is the same reconcile the nights already get when the dates change:
   * what survives keeps its day, what no longer exists is released — and released places are counted as
   * unplaced, which is where the user can do something about them.
   */
  useEffect(() => {
    setArrangement((previous) => {
      const kept: Record<number, string[]> = {};
      let changed = false;
      for (const [day, ids] of Object.entries(previous)) {
        if (Number(day) <= dayCount) kept[Number(day)] = ids;
        else changed = true;
      }
      return changed ? kept : previous;
    });
    // A time belongs to a (day, place) pair, so the ones for days that no longer exist go with them — and a
    // place re-added to a re-lengthened trip then arrives with no time rather than a stale answer.
    setSlots((previous) => {
      const kept: Record<string, TripSlot> = {};
      let changed = false;
      for (const [key, slot] of Object.entries(previous)) {
        if (Number(key.split("|")[0]) <= dayCount) kept[key] = slot;
        else changed = true;
      }
      return changed ? kept : previous;
    });
    setRestDays((previous) => {
      const kept = previous.filter((day) => day <= dayCount);
      return kept.length === previous.length ? previous : kept;
    });
  }, [dayCount]);

  /**
   * The lodgings the trip starts with: the first one the destination offers.
   *
   * The user's own rule read literally — the ordinary trip stays in one place — so the screen opens on that and
   * the per-night control appears only if they ask for it. An edit seeds from its own nights first, so this
   * never overwrites a stored answer.
   */
  useEffect(() => {
    if (lodgingChoices.length > 0) return;
    const first = rawPlaces.find((place) => place.categories.includes("stay"));
    if (first === undefined) return;
    setLodgingChoices([first.id]);
  }, [rawPlaces, lodgingChoices]);

  /**
   * A night whose lodging is no longer among the candidates, dropped.
   *
   * The ticked selection needed this too and does not any more, because it is derived from the destination
   * rather than remembered. A night *is* remembered, so it can still name a place the trip can no longer plan
   * with — the destination changed, or the place left the area — and an overnight point naming it would be
   * sent to the API and rejected.
   */
  useEffect(() => {
    // The stays in play, not every place: a night is a lodging, and a stay the user took out of the trip must
    // not stay attached to one.
    const available = new Set(chosenStays.map((place) => place.id));
    setNightLodgings((previous) => {
      const kept: Record<string, string | undefined> = {};
      let changed = false;
      for (const date of nightDates) {
        const place = previous[date];
        if (place === undefined) continue;
        if (available.has(place)) kept[date] = place;
        else changed = true;
      }
      if (!changed && Object.keys(kept).length === Object.keys(previous).length) return previous;
      return kept;
    });
  }, [chosenStays, nightDates]);

  // ---- Step 3 State: Generated Plans
  const [tripId, setTripId] = useState<string | null>(null);
  /*
   * The generated shape, not the stored one.
   *
   * `GeneratedPlanVariant` adds `pool_limited`, which describes this generation run and is not stored —
   * it stops being true as soon as the user saves another place. Typing the state as the stored shape
   * would have quietly dropped it, which is what happened: the flag was computed by `core`, carried
   * through the service, returned by the handler, and then discarded here by a type.
   */
  const [variants, setVariants] = useState<GeneratedPlanVariant[]>([]);

  /**
   * The strategy the user picked, when there was no arrangement to decide for them.
   *
   * Only read while `hasArrangement` (above) is false. Null means "not picked yet", which falls back to the
   * pace's own strategy — the same nudge step 1 already gives.
   */
  const [strategyPick, setStrategyPick] = useState<PlanVariant["strategy"] | null>(null);

  /**
   * The plan the screen shows.
   *
   * With an arrangement it is the one the user's own pace asked for: measured, all three variants carried
   * character-identical day chains and the same 12 điểm / 88.2 km / 111 phút, differing only in rest hours,
   * so a choice between them is not a choice. Without an arrangement the strategies really do differ, and
   * the chip row above the card lets the user pick one — the pace's own strategy is the default, not the
   * only answer. `PACE_TO_STRATEGY` is that default mapping; `?? variants[0]` is the fallback for a
   * generation whose strategy set does not contain it, because showing the engine's own first plan beats
   * showing nothing.
   */
  const paceStrategy = PACE_TO_STRATEGY[pace];
  const shownStrategy = hasArrangement ? paceStrategy : (strategyPick ?? paceStrategy);
  const planVariant = variants.find((variant) => variant.strategy === shownStrategy) ?? variants[0] ?? null;

  const [routingStatus, setRoutingStatus] = useState<ProviderStatus | null>(null);
  /**
   * Each variant's days, so the card shows the itinerary and not only its totals.
   *
   * Three more requests, in parallel with the explanations the screen already fetches. The alternative —
   * a card that lists "42.9 km · 53 phút" and leaves the user to guess which stops that means — is the
   * version this replaced, and a distance is not a trip: two plans with the same total are different
   * trips if one of them visits the lake first.
   */
  const [variantDetails, setVariantDetails] = useState<Record<string, PlanVariantDetail>>({});
  const [explanations, setExplanations] = useState<Record<string, PlanExplanation[]>>({});
  const [planDetail, setPlanDetail] = useState<PlanVariantDetail | null>(null);

  /**
   * The shown plan's own parts, read once here rather than inside the JSX.
   *
   * The two keyed maps may have no entry for a variant — each fetch uses `allSettled`, so one failed request
   * leaves the card with figures and no chain rather than failing the step. That absence is a state the render
   * handles, not an error, and reading it here keeps the JSX to one expression per fact.
   */
  const shownPlanDetail = planVariant === null ? undefined : variantDetails[planVariant.id];
  const shownPlanNumbers =
    planVariant === null
      ? []
      : [
          { value: String(planVariant.place_count), label: "điểm" },
          { value: formatKm(planVariant.total_distance_m), label: "quãng đường" },
          { value: formatDriving(planVariant.total_driving_s), label: "lái xe" },
          { value: formatRestHours(planVariant.total_rest_s), label: "nghỉ" },
        ];
  const shownPlanExplanations = planVariant === null ? [] : explanations[planVariant.id] ?? [];

  /**
   * Step 4's heading: where the trip goes, when it is, and which plan is drawn.
   *
   * It interpolated `destination`, which only an existing trip being edited ever fills — a new trip chooses
   * an *area*, and the area's own name is what `tripInput` writes as the trip's destination. Measured on two
   * runs, the last screen before saving read `Lộ trình — Thư giãn`, the separator hanging over nothing, while
   * the trip being saved was `Chuyến đi Đà Lạt`. The area's name is the fact there.
   *
   * The dates come with it because this is the end of the flow and the only screen in it that never said
   * when the trip is. They are the same two fields the save writes, read through `tripDateRange` — the one
   * format the trips list and the trip's own page use — so the last thing read before saving agrees with the
   * first thing read after it.
   */
  const roadmapHeading =
    planDetail === null
      ? null
      : `Lộ trình ${destinationName} — ${planDetail.variant.label} · ${
          tripDateRange(startDate, endDate) ?? copy.state.unknown
        }`;

  /**
   * Which day the roadmap is showing.
   *
   * One day at a time rather than one map per day. Three Leaflet instances on one page is three tile
   * layers and three sets of event handlers competing for the main thread on a mid-range Android, and
   * Leaflet is already the largest thing this app ships (150 kB, split into its own chunk). The day
   * switcher above the map carries every day's distance so the whole trip is still legible at a glance,
   * and the map shows the detail of the one being read.
   *
   * Reset to the first day whenever the plan changes, because a day index from the previous plan may not
   * exist in the new one — which would leave the map blank with no explanation.
   */

  /**
   * The arranging step's forward action performs the two writes in order: a plan belongs to a trip, so the
   * trip must exist first. Every figure the option cards show then arrives on the variants (spec §6.5) —
   * this component computes none. A `null` metric means the routing provider did not answer and renders as
   * "chưa xác định"; this screen once supplied plausible stand-ins for exactly these numbers, which is what
   * apps/measurement-honesty.test.ts exists to prevent.
   *
   * It is also what the review step calls on entry, when it arrives with no variants to read — see the
   * effect below. One generator, one status: whatever step is on screen, the run is this mutation's.
   */
  const generatePlans = useMutation({
    mutationFn: async () => {
      // Only the pace is sent, and it is still chosen on step 1. The other constraint fields take the
      // contract's defaults for a new trip and are left untouched on an existing one — the constraint screen
      // that offered them is gone, and the plan handler reads them from the trip's own row.
      const withConstraints = {
        ...tripInput(),
        constraints: { pace },
      };
      /*
       * One trip, whether or not the arrangement step already saved one.
       *
       * Before arranging, this screen created a trip here. Now the arrangement step creates it first — it has
       * to, the arrangement is stored on a trip — so generating updates that trip instead. Creating a second
       * one would leave the arrangement on an orphan while the plan lived elsewhere, which is the same class
       * of duplicate the two writers of a trip could already produce and the reason `tripInput` exists.
       */
      const trip = await persistTrip(withConstraints);
      const newTripId = trip.trip.id;
      setArrangedTripId(newTripId);
      const plans = await client.createPlans({ id: newTripId });
      // A reason without evidence is not shown: reasons come from the explanations endpoint, and a
      // variant whose fetch fails shows none rather than a placeholder sentence.
      const fetched = await Promise.allSettled(
        plans.variants.map((variant) =>
          client.getPlanExplanations({ id: newTripId, variantId: variant.id }),
        ),
      );
      const byVariant: Record<string, PlanExplanation[]> = {};
      plans.variants.forEach((variant, index) => {
        const result = fetched[index];
        if (result?.status === "fulfilled") byVariant[variant.id] = result.value.explanations;
      });

      /*
       * The days, one request per variant, in parallel.
       *
       * `allSettled` for the same reason the explanations use it: one variant whose detail fails must
       * leave that card without an itinerary, not fail the whole step. A card with no chain is a real
       * state the render handles; three cards lost because one request timed out is not.
       */
      const dayFetches = await Promise.allSettled(
        plans.variants.map((variant) => client.getPlanVariant({ id: newTripId, variantId: variant.id })),
      );
      const details: Record<string, PlanVariantDetail> = {};
      plans.variants.forEach((variant, index) => {
        const result = dayFetches[index];
        if (result?.status === "fulfilled") details[variant.id] = result.value;
      });

      return { tripId: newTripId, plans, byVariant, details };
    },
    onSuccess: ({ tripId, plans, byVariant, details }) => {
      /*
       * The trip has plans now, so it is finished work: it is on the trips list and it is opened with the edit
       * route. The next `/trips/new` is a new trip, which is what its own name says — and this is what stops
       * the anchor from quietly making the next new trip an edit of this one. The session keeps its id, so
       * going back a step and arranging again still updates the same trip.
       */
      writeTripAnchor(null, Date.now(), typeof window === "undefined" ? null : window.localStorage);
      setTripId(tripId);
      setVariants(plans.variants);
      setStrategyPick(null);
      setRoutingStatus(plans.routing_status);
      setExplanations(byVariant);
      setVariantDetails(details);
    },
  });

  /*
   * Arriving at the review step is what asks for a plan.
   *
   * The step used to wait to be asked: it rendered a "Tạo lộ trình" button whenever it held no variants, and
   * a user who reached it with none — a resumed draft, a generation that failed before the button was ever
   * pressed — stared at a button whose absence of plans nothing explained. The generation is the arranging
   * step's act now, and this is the step's own guarantee that there is always something to review: entry
   * with no variants and nothing running starts the run. It is idempotent by the two guards below — a run
   * already in flight (`isPending`) or already failed (`isError`, whose alert and retry own that state) is
   * not restarted — so a re-render, a StrictMode double-invoke, or a return to the step after a failure
   * cannot stack runs.
   *
   * `generationRequested` closes the window between the render that decides to ask and the render that
   * observes `isPending`: without it a synchronous double-invoke of the effect would ask twice.
   */
  const generationRequested = useRef(false);
  useEffect(() => {
    if (step !== 3) return;
    if (variants.length > 0) {
      generationRequested.current = false;
      return;
    }
    if (generatePlans.isPending || generatePlans.isError) return;
    if (generationRequested.current) return;
    generationRequested.current = true;
    generatePlans.mutate();
    // `generatePlans` is deliberately absent: the mutation object is not stable across renders, and every
    // state that decides whether to ask is already a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- state guards above are the whole decision.
  }, [step, variants.length, generatePlans.isPending, generatePlans.isError]);

  /** Choosing a variant marks it selected server-side and loads its days for the roadmap step. */
  const openVariant = useMutation({
    mutationFn: async (variantId: string) => {
      if (tripId === null) {
        // Unreachable through the UI: step 4 renders only after generatePlans has set the trip id.
        throw new Error("no trip exists to select a plan for");
      }
      await client.selectPlan({ id: tripId, variantId });
      return client.getPlanVariant({ id: tripId, variantId });
    },
    onSuccess: (detail) => {
      setPlanDetail(detail);
      // Day 1 rather than whatever day was being read: the previous plan's index may not exist in this
      // one, and a blank map with no explanation is worse than losing the user's place in a three-day
      // list. `PlanRoadmap` owns that state now, so being keyed by variant id is what resets it.
      setStep(4);
    },
  });

  // Auto-save draft to sessionStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const draft = {
        step,
        destination,
        startDate,
        endDate,
        adults,
        children,
        transport,
        transportIsOther,
        transportNote,
        pace,
        stayLastNight,
        nightLodgings,
        entryPlaceId,
        arrangement,
        slots,
        arrangedTripId,
        lodgingChoices,
        restDays,
        destinationAreaId,
      };
      window.sessionStorage?.setItem(draftKey, JSON.stringify(draft));
    } catch {
      // Ignore private mode or quota errors
    }
  }, [
    step,
    destination,
    startDate,
    endDate,
    adults,
    children,
    transport,
    transportIsOther,
    transportNote,
    pace,
    stayLastNight,
    nightLodgings,
    entryPlaceId,
    arrangement,
    slots,
    arrangedTripId,
    lodgingChoices,
    restDays,
    destinationAreaId,
    draftKey,
  ]);

  /**
   * Add or remove one place on one day. The same place on two days is two calls, and both are kept.
   *
   * Both setters are called from the handler rather than one inside the other's updater. The first version
   * called `setSlots` from inside `setArrangement`'s function, which React documents as unsafe: an updater
   * must be pure, it may be invoked more than once, and this one's second invocation would have found the
   * arrangement already changed. Reading the two pieces of state from the render instead is exact, because a
   * click always sees the values the user is looking at.
   */
  function togglePlaceDay(placeId: string, day: number): void {
    const current = arrangement[day] ?? [];
    const on = current.includes(placeId);
    const next = on ? current.filter((id) => id !== placeId) : [...current, placeId];

    const updated = { ...arrangement, [day]: next };
    // A day with nothing on it is an empty day, not an absent one — the same distinction the engine makes.
    if (next.length === 0) delete updated[day];
    setArrangement(updated);

    // The slot belongs to the pair, so taking the place off the day takes its time with it. Left behind, it
    // would be a time for a stop that does not exist — and a place returning to that day would silently come
    // back holding an answer the user never chose again.
    if (on) {
      const key = `${day}|${placeId}`;
      if (slots[key] !== undefined) {
        const remaining = { ...slots };
        delete remaining[key];
        setSlots(remaining);
      }
    }
  }

  function setPlaceSlot(placeId: string, day: number, slot: TripSlot): void {
    const key = `${day}|${placeId}`;
    setSlots((previous) => {
      // Tapping the chosen slot clears it: the user can always take back a time they gave.
      if (previous[key] === slot) {
        const remaining = { ...previous };
        delete remaining[key];
        return remaining;
      }
      return { ...previous, [key]: slot };
    });
  }

  function resetPlanner(): void {
    if (typeof window !== "undefined") {
      window.sessionStorage?.removeItem(draftKey);
    }
    setStep(1);
    setDestination("");
    const d = defaultDates();
    setStartDate(d.start);
    setEndDate(d.end);
    setAdults(2);
    setChildren(2);
    setTransport("car");
    setPace("balanced");
    setDestinationAreaId(areaId);
    setStayLastNight(false);
    setArrangement({});
    setRestDays([]);
    setSlots({});
    setArrangeDay(1);
    setNightLodgings({});
    setEntryPlaceId(null);
    /*
     * The trip the arrangement was last saved to, and why starting over has to forget it.
     *
     * It exists so that returning to this step continues the same trip instead of making a second one — but
     * "Đặt lại" means start over from nothing, and this was the one piece of state it left behind. The next
     * save then *updated* the trip the user had just abandoned, and the update carries the new title, dates,
     * destination, places and arrangement: measured, planning Đà Lạt and then resetting to arrange Vũng Tàu
     * rewrote the Đà Lạt trip and left no error anywhere. The button says it clears the draft; this is the
     * half of the draft that lives in state.
     */
    setArrangedTripId(null);
    // Stated here as well as in the effect above, because the effect's write is what removes the stored copy
    // and a browser that closes between the two would keep an anchor to the trip it just abandoned.
    writeTripAnchor(null, Date.now(), typeof window === "undefined" ? null : window.localStorage);
    // And the abandatory's lodging, which is a place id from the destination the user is leaving: kept, it
    // suppresses the new destination's own default (the effect below only fires while this is empty).
    setLodgingChoices([]);
    // The generated trip and variants belong to the abandoned plan; starting over must not leave
    // step 3 rendering them as if they answered the reset inputs.
    setTripId(null);
    setVariants([]);
    setStrategyPick(null);
    setRoutingStatus(null);
    setExplanations({});
    setPlanDetail(null);
    setVariantDetails({});
    /*
     * And the generation run itself, which is state of its own: an error left on the mutation would put its
     * alert on the fresh arrangement the moment the reset lands on step 1, and the next press would look
     * like a retry of an abandoned trip. `reset` clears the status and lets the next press start clean.
     */
    generatePlans.reset();
  }


  // The trip an edit is based on, before there is a form to show. A 404 is not an error state to recover
  // from: the only fix is to pick another trip, so it points at the list.
  if (isEditing && editingQuery.isPending) {
    return (
      <div className="screen">
        <p className="state">{copy.state.loading}</p>
      </div>
    );
  }
  if (isEditing && (editingQuery.isError || editingQuery.data === undefined)) {
    return (
      <div className="screen">
        <div className="state">
          <p className="state__desc">{copy.trips.tripNotFound}</p>
          <button type="button" className="button" onClick={() => navigate("/trips")}>
            {copy.trips.heading}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen planner-screen">
      {/* Step Header */}
      {/*
        The head, in two rows because it had five.

        Measured on a 390px screen, the arrange step stacked its stepper, its title, its two actions, a status
        chip, a paragraph of rules and a tab bar before the first place could be touched — about a third of the
        window spent on chrome. The stepper and its two secondary actions share one line, and everything else
        the step needs sits on the second: the page's name, what is left to decide, the rules one tap away, and
        the one action that decides the rest. The rows are fewer and each of them is shorter, which is what
        the space was for.
      */}
      {/*
        The head: the page's name first, then the steps.

        Two things moved. The title now leads, because every other screen in this app leads with its title and a
        planner that opened with a progress bar was the one screen whose name you had to look for. And the two
        ways out of the step — Đặt lại, Quay lại — sit at the end of the title's own row rather than on a line of
        their own: they are small secondary controls, and a row of their own gave them a weight the step's own
        action should have had instead.

        The steps keep a row, and the step's one action shares it. Measured at 390px: the head is 104px and the
        day being arranged starts at 219, against 189 and 304 for the version before this — and 502 for the one
        before that, where the title sat under the bar and both had a row of their own.
      */}
      <div className="screen__head planner-screen__head" ref={measureHead}>
        <div className="screen__head-title">
          <h1 className="screen__heading">
            {step === 1 && "Chuyến đi này thế nào?"}
            {step === 2 && "Xếp địa điểm"}
            {step === 3 && copy.planner.reviewHeading}
            {step === 4 && roadmapHeading}
          </h1>

          {/*
            The rules, as an icon that opens over the page.

            A tooltip was what the brief asked for and this is what a phone can use: nothing hovers on one, and a
            `title` attribute is unreachable by touch and by keyboard. The panel is absolutely positioned, so
            opening it costs no layout — the row it hangs from does not grow. Every rule the step used to state
            in prose is still here, in one place, for the one reading.
          */}
          {step === 2 && (
            <details className="arrange__help">
              <summary className="arrange__help-summary" aria-label="Cách xếp ngày">
                ℹ️
              </summary>
              <p className="arrange__help-text">
                Chạm vào một chỗ trong kho để thêm hoặc bỏ khỏi ngày đang mở. Kéo tay nắm ⠿ để đổi thứ tự trong
                ngày, hoặc kéo một chỗ từ kho lên thẻ ngày để xếp nó vào ngày đó. Bản đồ và danh sách vẽ theo đúng
                thứ tự bạn xếp, và lộ trình tạo ra cũng đi theo thứ tự đó — hệ thống không tự xếp lại ngày của
                bạn. Riêng ngày bạn đã đặt giờ thì đi theo thứ tự các buổi trong ngày (sáng, trưa, chiều, tối),
                vì đó là thứ tự chính bạn nói. Muốn đi theo thứ tự ngắn nhất thì bấm nút gợi ý dưới bản đồ, và
                nút đó chỉ hiện với ngày chưa đặt giờ. Số km trong kho và trên thẻ ngày là khoảng cách đường chim
                bay.
              </p>
            </details>
          )}

          <div className="screen__head-actions">
            {step > 1 && (
              /*
                Confirmed, not immediate.

                The chip is small, secondary and sits next to "Quay lại" — so it is reached by a thumb on a
                moving bus as easily as it is by intent, and what it destroys is everything the user has typed
                since they opened the screen. It opens the dialog below rather than resetting.
              */
              <button
                type="button"
                className="chip chip--sm"
                title="Xoá bản nháp và bắt đầu lại từ đầu"
                onClick={() => setResetOpen(true)}
              >
                🔄 Đặt lại
              </button>
            )}
            {/*
              The step's only back control.

              It used to be one of two: each pinned bar carried its own "Quay lại" as well, and on the
              constraint step (since removed) that lower one was wired to `setStep(3)` — the step it was
              already on — so it rendered, took the tap and changed nothing (measured: `elementFromPoint` at
              its centre returned the button, and the head's own returned step 2). The head's is the one that
              stays, because it is sticky on every step and is already the only back control on the steps with
              no pinned bar; the bars now hold their forward action alone. The label is the two things it
              really does: step back after the first step, leave the planner on it — `goBack` follows history
              when the screen was opened from one, so the exit promises no destination.
            */}
            <button
              type="button"
              className="link"
              onClick={() => (step > 1 ? setStep((step - 1) as 1 | 2 | 3 | 4) : goBack())}
            >
              {step === 1 ? copy.planner.exit : copy.planner.backShort}
            </button>
          </div>
        </div>

        <div className="screen__head-top">
          <div className="planner-steps" role="group" aria-label="Các bước của chuyến đi">
            {PLANNER_STEPS.map((name, index) => {
              const number = index + 1;
              const state = number < step ? "done" : number === step ? "current" : "todo";
              return (
                <div
                  key={name}
                  className={STEP_ITEM_CLASS[state]}
                  {...(state === "current" ? { "aria-current": "step" as const } : {})}
                >
                  <span className="planner-steps__bar" />
                  <span className="planner-steps__name">{name}</span>
                </div>
              );
            })}
          </div>

        </div>
      </div>

      {/* ======================= STEP 1: TRIP SETUP ======================= */}
      {step === 1 && (
        <div className="form form--trip">
          {/*
            Said on the step the fields are on, not only in the URL.

            An edit and a create look identical from here, and the consequence of not knowing which one you are
            in is the whole trip: a user who thinks they are making a new one will not expect their changes to
            land on the one they opened.
          */}
          {isEditing && <p className="field__help">{copy.trips.editingNote}</p>}

          {/*
            Three cards, one per decision, rather than one flat column of seven questions.

            The screen asks three things — where and when, who and how, and how densely — and the flat version
            made them look like one long form to be got through. The grouping is also what the spacing is for:
            the gaps inside a card say "these belong together" and the gaps between cards say "this is a new
            question", which one uniform gap cannot say at all.
          */}
          <section className="trip-card">
            <h2 className="trip-card__title">Thông tin cơ bản</h2>

            {/*
              The destination is chosen from the areas the user already built, and that choice is what decides
              the places: every place filed under it is a candidate for the arrange step.

              A select and not a text box. A typed destination is a string that matches no place, and a trip to it
              plans nothing — where an area is a real, stored grouping with a known membership. Each option
              carries its count for the same reason: "Đà Lạt · 13 địa điểm" says whether the trip has anything to
              plan with before it is chosen.
            */}
            <label className="field">
              <span className="field__label">Đi đâu?</span>
              <select
                className="field__input"
                value={destinationAreaId}
                onChange={(event) => setDestinationAreaId(event.target.value)}
              >
                <option value="">— chọn khu vực đã lưu —</option>
                {areas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name} · {area.count} địa điểm
                  </option>
                ))}
              </select>
            </label>

            {destinationAreaId === "" && (
              <p className="field__help">
                {areas.length === 0
                  ? "Bạn chưa có khu vực nào. Khu vực được tạo khi bạn lưu địa điểm và điền khu vực cho nó."
                  : "Chọn một khu vực để lấy danh sách địa điểm của chuyến."}
              </p>
            )}

            {/*
              Places with no area, counted where the choice is made.

              The destination is an area, so a place filed under none is reachable by no destination — and a place
              the user cannot find anywhere is worse than one the app says it cannot reach yet.
            */}
            {unplacedByArea > 0 && destinationAreaId !== "" && (
              <p className="field__help">
                {unplacedByArea} địa điểm chưa thuộc khu vực nào nên không nằm trong chuyến này.{" "}
                <Link className="link" to="/places">
                  Gán khu vực cho chúng
                </Link>
              </p>
            )}

            {/*
              The dates, as one range rather than two boxes.

              Two `input type=date` fields made the user answer a question that is one answer: a trip is a
              beginning and an end, and the second date is meaningless without the first. The calendar is ours
              rather than the browser's because the range is what is being chosen — a native picker cannot know
              that the day it is opening is the far end of a range already started.
            */}
            <RangeCalendar
              label="Khoảng ngày của chuyến"
              start={startDate}
              end={endDate}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />

            {/*
              Days and nights, said out loud, and the one thing the dates cannot express.

              "3 ngày 2 đêm" is how a Vietnamese traveller describes a trip, and a number the screen never
              states is a number nobody can correct. Going home on the last day, or sleeping it and leaving
              the next morning, is the same pair of dates — so it is a question of its own, and a segmented
              control because two loose chips read like filter tags and this one changes the plan.
            */}
            <div className="field">
              <span className="field__label" id="last-night-label">
                Đêm cuối ngủ ở đâu?
              </span>
              <div className="segmented" role="group" aria-labelledby="last-night-label">
                <button
                  type="button"
                  className={!stayLastNight ? "segmented__option segmented__option--on" : "segmented__option"}
                  aria-pressed={!stayLastNight}
                  onClick={() => setStayLastNight(false)}
                >
                  Về trong ngày cuối
                </button>
                <button
                  type="button"
                  className={stayLastNight ? "segmented__option segmented__option--on" : "segmented__option"}
                  aria-pressed={stayLastNight}
                  onClick={() => setStayLastNight(true)}
                >
                  🛏️ Ở lại đêm cuối
                </button>
              </div>
              <p className="field__help">
                {dayCount} ngày · {nightDates.length} đêm.
              </p>
            </div>
          </section>

          {/*
            Chỗ ở, on the first screen.

            It was decided two steps later, in the arrange step's own column, which is the wrong place for two
            reasons. It is a decision about the *trip* — which beds the nights are spent in, and therefore where
            every later morning starts — and the first screen is where the trip is described. And it was hidden
            behind a condition on the arrange step that most trips never reached: with one lodging in the
            destination the chips did not render at all, so a user who had saved one homestay had no way to see
            that the trip was already using it.

            One lodging is the trip's, and says so in a sentence rather than a chip that would be the only chip.
            Two or more is a real choice, and then the chips appear with the per-night rows they exist for.
          */}
          {destinationAreaId !== "" && (
            <section className="trip-card">
              <h2 className="trip-card__title">Chỗ ở</h2>
              {staysInDestination.length === 0 && (
                <p className="field__help">
                  Khu vực này chưa có chỗ ở nào. Lưu một homestay hay khách sạn rồi chọn nó ở đây.
                </p>
              )}

              {staysInDestination.length > 0 && (
                <>
                  {staysInDestination.length > 1 && (
                    <div className="stays">
                      <div className="chips">
                        {staysInDestination.map((stay) => {
                          const on = lodgingChoices.includes(stay.id);
                          return (
                            <button
                              key={stay.id}
                              type="button"
                              className={on ? "chip chip--sm chip--on" : "chip chip--sm"}
                              aria-pressed={on}
                              onClick={() =>
                                setLodgingChoices((previous) =>
                                  on ? previous.filter((id) => id !== stay.id) : [...previous, stay.id],
                                )
                              }
                            >
                              {stay.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {chosenStays.length === 1 && nightDates.length > 0 && (
                    <p className="field__help">
                      Ở {chosenStays[0]?.name} cả {nightDates.length} đêm.
                    </p>
                  )}

                  {chosenStays.length === 0 && (
                    <p className="field__help">
                      Chọn ít nhất một chỗ ở — nếu không, mỗi ngày không có điểm neo và lộ trình sẽ không có
                      chỗ bắt đầu.
                    </p>
                  )}

                  {chosenStays.length > 1 &&
                    nightDates.map((date) => (
                      <label key={date} className="nights__row">
                        <span className="nights__date">Đêm {formatShortDate(date)}</span>
                        <select
                          className="field__input nights__select"
                          value={nightLodgings[date] ?? ""}
                          onChange={(event) =>
                            setNightLodgings((previous) => ({
                              ...previous,
                              [date]: event.target.value === "" ? undefined : event.target.value,
                            }))
                          }
                        >
                          <option value="">— chưa chọn —</option>
                          {chosenStays.map((stay) => (
                            <option key={stay.id} value={stay.id}>
                              {stay.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    ))}
                </>
              )}
            </section>
          )}

          <section className="trip-card">
            <h2 className="trip-card__title">Thành viên và phương tiện</h2>

            <div className="field">
              <span className="field__label" id="party-label">
                Mấy người đi?
              </span>
              {/*
                Presets first, exact counts under them. The presets are what most trips are and they cost one
                tap; the counters are for everyone else — and before them a party of three had to pick
                "2 lớn · 2 trẻ em" and be wrong in two places at once.
              */}
              <div className="chips" role="group" aria-labelledby="party-label">
                <button
                  type="button"
                  className={`chip ${adults === 2 && children === 2 ? "chip--on" : ""}`}
                  onClick={() => { setAdults(2); setChildren(2); }}
                >
                  👨‍👩‍👧‍👦 2 lớn · 2 trẻ em
                </button>
                <button
                  type="button"
                  className={`chip ${adults === 2 && children === 0 ? "chip--on" : ""}`}
                  onClick={() => { setAdults(2); setChildren(0); }}
                >
                  👫 2 người lớn
                </button>
                <button
                  type="button"
                  className={`chip ${adults === 1 && children === 0 ? "chip--on" : ""}`}
                  onClick={() => { setAdults(1); setChildren(0); }}
                >
                  👤 1 mình
                </button>
                <button
                  type="button"
                  className={`chip ${adults === 4 && children === 0 ? "chip--on" : ""}`}
                  onClick={() => { setAdults(4); setChildren(0); }}
                >
                  👥 Nhóm bạn (4 người)
                </button>
              </div>

              {/*
                Counters, not selects. The selects listed twenty of everything, which put the answer people
                actually give — one more than last time — twenty rows down a scroll, and on a phone the wheel
                covered the field it was changing.
              */}
              <div className="field-pair">
                <div className="field">
                  <span className="field__label" id="adults-label">
                    Người lớn
                  </span>
                  <div className="stepper" role="group" aria-labelledby="adults-label">
                    <button
                      type="button"
                      className="stepper__button"
                      aria-label="Bớt một người lớn"
                      disabled={adults <= 1}
                      onClick={() => setAdults((count) => Math.max(1, count - 1))}
                    >
                      −
                    </button>
                    <span className="stepper__value" role="status">
                      {adults}
                    </span>
                    <button
                      type="button"
                      className="stepper__button"
                      aria-label="Thêm một người lớn"
                      disabled={adults >= MAX_ADULTS}
                      onClick={() => setAdults((count) => Math.min(MAX_ADULTS, count + 1))}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="field">
                  <span className="field__label" id="children-label">
                    Trẻ em
                  </span>
                  {/*
                    Zero is a real answer and the floor, because most trips have none — and "không có trẻ em"
                    is a fact the plan uses: the caps and the pace read differently for a party that has them.
                  */}
                  <div className="stepper" role="group" aria-labelledby="children-label">
                    <button
                      type="button"
                      className="stepper__button"
                      aria-label="Bớt một trẻ em"
                      disabled={children <= 0}
                      onClick={() => setChildren((count) => Math.max(0, count - 1))}
                    >
                      −
                    </button>
                    <span className="stepper__value" role="status">
                      {children}
                    </span>
                    <button
                      type="button"
                      className="stepper__button"
                      aria-label="Thêm một trẻ em"
                      disabled={children >= MAX_CHILDREN}
                      onClick={() => setChildren((count) => Math.min(MAX_CHILDREN, count + 1))}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="field">
              <span className="field__label" id="transport-label">
                Đi bằng gì?
              </span>
              {/*
                Every member of the enum the screen can name, not three of five: `bicycle` and
                `public_transport` were declared in the contract and unreachable from here, so a user arriving
                by coach had to answer "ô tô" or "đi bộ".

                Cards rather than chips: five chips wrap into two rows on a phone and become a row of words to
                hit, while these stay a grid of targets — and a miss here changes how far the plan thinks a day
                can reach.
              */}
              <div className="option-grid" role="group" aria-labelledby="transport-label">
                {(
                  [
                    ["car", "🚗", "Ô tô"],
                    ["motorbike", "🛵", "Xe máy"],
                    ["bicycle", "🚲", "Xe đạp"],
                    ["walk", "🚶", "Đi bộ"],
                    ["public_transport", "🚌", "Xe khách / tàu"],
                  ] as const
                ).map(([mode, glyph, label]) => (
                  <button
                    key={mode}
                    type="button"
                    className={`option-card ${!transportIsOther && transport === mode ? "option-card--on" : ""}`}
                    aria-pressed={!transportIsOther && transport === mode}
                    onClick={() => {
                      setTransportIsOther(false);
                      setTransport(mode);
                    }}
                  >
                    <span className="option-card__glyph" aria-hidden="true">
                      {glyph}
                    </span>
                    <span>{label}</span>
                  </button>
                ))}
                <button
                  type="button"
                  className={`option-card ${transportIsOther ? "option-card--on" : ""}`}
                  aria-pressed={transportIsOther}
                  onClick={() => {
                    setTransportIsOther(true);
                    setTransport("public_transport");
                  }}
                >
                  <span className="option-card__glyph" aria-hidden="true">
                    ✏️
                  </span>
                  <span>Khác</span>
                </button>
              </div>

              {/*
                The input appears with the choice, not instead of it — and also when there is something to show:
                a restored trip can carry a note beside a named mode, and a field that hid it would be a value
                the screen holds and never displays.
              */}
              {(transportIsOther || transportNote.trim() !== "") && (
                <div className="field">
                  <label className="field">
                    <span className="field__label">Đi bằng gì? (tự mô tả)</span>
                    <input
                      className="field__input"
                      value={transportNote}
                      onChange={(e) => setTransportNote(e.target.value)}
                      placeholder="Ví dụ: thuê xe 7 chỗ có tài"
                      maxLength={200}
                    />
                  </label>
                  <p className="field__help">
                    Lộ trình vẫn tính theo phương tiện gần nhất (xe khách / tàu), vì máy không đọc được tốc
                    độ từ mô tả.
                  </p>
                </div>
              )}
            </div>
          </section>

          <section className="trip-card">
            <h2 className="trip-card__title">Phong cách chuyến đi</h2>

            {/*
              The pace, with what it does to the plan written on it.

              The numbers are the planner's own (`PLACES_PER_DAY` in `apps/api/src/trips/planner.ts`: 3, 5 and
              7 places a day, which each of the three generated options then scales between 0.67x and 1.33x).
              They are on the cards because the choice is otherwise a mood — "dày đặc" and "thư giãn" are
              adjectives, and the thing the user is actually choosing is how many places a day holds.
            */}
            <span className="field__label" id="pace-label">
              Nhịp độ chuyến đi
            </span>
            <div className="option-grid" role="group" aria-labelledby="pace-label">
              {(
                [
                  ["relaxed", "😌", "Thư giãn", "khoảng 3 địa điểm/ngày"],
                  ["balanced", "⚖️", "Cân bằng", "khoảng 5 địa điểm/ngày"],
                  ["packed", "🏃", "Dày đặc", "khoảng 7 địa điểm/ngày"],
                ] as const
              ).map(([value, glyph, label, note]) => (
                <button
                  key={value}
                  type="button"
                  className={`option-card ${pace === value ? "option-card--on" : ""}`}
                  aria-pressed={pace === value}
                  onClick={() => setPace(value)}
                >
                  <span className="option-card__glyph" aria-hidden="true">
                    {glyph}
                  </span>
                  <span>{label}</span>
                  <span className="option-card__note">{note}</span>
                </button>
              ))}
            </div>
          </section>

          <div className="savebar" ref={measureSavebar}>
            {/*
              Disabled until the trip has somewhere to go, and the label does not promise a count it does not
              have: the old one read "Tiếp: Xếp 0 địa điểm vào ngày", which is a number the user cannot act on
              from here — the places come from the area, one step earlier.
            */}
            <button
              type="button"
              className="button button--primary button--save"
              disabled={destinationAreaId === ""}
              onClick={() => setStep(2)}
            >
              Tiếp: Xếp địa điểm vào ngày
            </button>
          </div>
        </div>
      )}


      {/* ======================= STEP 2: ARRANGE THE DAYS ======================= */}
      {step === 2 && (
        <div className="arrange">
          {/*
            Nothing to arrange, said rather than shown as a blank.

            Two ways to arrive here with no candidates: no destination was kept — a draft from before the
            destination existed, which is how this was found, coming back after a reload with day tabs and no
            rows — or the destination has no places in it. Either way an empty list is indistinguishable from a
            broken screen, and the fix is one step back.
          */}
          {rawPlaces.length === 0 ? (
            <div className="state">
              <p className="state__desc">
                {destinationAreaId === ""
                  ? "Chuyến này chưa có điểm đến nên chưa có địa điểm nào để xếp."
                  : `Khu vực "${destinationName}" chưa có địa điểm nào.`}
              </p>
              <button type="button" className="button button--primary" onClick={() => setStep(1)}>
                Chọn điểm đến
              </button>
            </div>
          ) : (
            <>
              {/*
                What is left to decide, and the one action that decides it.

                This header replaced four lines of instructions and two warnings. All of that text was true and
                none of it was read: measured at 390px the instructions were the first four lines of body text
                on the screen, and "12 / 12 địa điểm chưa xếp ngày nào" sat under a list long enough that the
                user had already scrolled past where it was written. The count is a chip, the rules are one tap
                away, and the action that changes the count sits beside the count rather than describing
                itself in a sentence.
              */}
              {/*
                The days, as tabs rather than as chips with a count tacked onto them.

                A chip row answers "how full is the trip". This is navigation, and it has to answer which day
                you are looking at as well as which days exist — so the current tab carries the accent's tint
                and the deep accent label, the treatment the kit gives a selected chip, and every tab carries
                its own size, which is the one fact a one-day-at-a-time screen otherwise loses. The row scrolls
                rather than wraps, because a nine-day trip wrapping to three rows is three rows of navigation
                above the thing being navigated.
              */}
              <div className="daytabs" ref={measureRail}>
                {/*
                  The step's one action, at the head of the rail, and only while there is nothing to lose.

                  It sat at the end of the progress bar's row, pushed against the right edge with the bar's five
                  names between it and anything it acted on — a reviewer read it as unbalance, and they were
                  right: the action belongs to the days, not to the steps. Here it is the first thing on the row
                  the days are on, and above the rail on a phone, where the row wraps and the button takes the
                  line it needs rather than squeezing the tabs into one and a half.

                  Gone once anything is arranged. The re-split that used to appear here rebuilt the days from
                  `selectedPlaces` alone, so a stay and a place filed under another area — both on a day before —
                  were dropped, and the confirm named only the arrangement being replaced. The action is the
                  loss, so the action is what left; a fresh trip has nothing to replace and still gets the split.
                */}
                {!hasArrangement && (
                  <button type="button" className="button button--primary arrange__auto" onClick={autoAssignDays}>
                    {copy.planner.arrangeSplit}
                  </button>
                )}
                {/*
                  The two steps beside the rail.

                  A tab bar with nine days scrolls the last ones out of reach, and a user working through a trip
                  in order moves one day at a time far more often than they jump — so the ends get an arrow each,
                  sized to the tap floor. Not instead of the rail: alone they would hide which days are empty and
                  how heavy the others are, which is the one thing this step has to be able to see at once.
                */}
                <button
                  type="button"
                  className="daytabs__step"
                  aria-label="Ngày trước"
                  disabled={activeArrangeDay <= 1}
                  onClick={() => selectArrangeDay(activeArrangeDay - 1)}
                >
                  ‹
                </button>
                <div className="daytabs__rail" role="tablist" aria-label="Chọn ngày để xếp">
                  {Array.from({ length: dayCount }, (_, index) => index + 1).map((day) => {
                    const count = (arrangement[day] ?? []).length;
                    const active = day === activeArrangeDay;
                    const span = dayStraightLineMetres(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        role="tab"
                        id={`arrange-tab-${day}`}
                        aria-controls={`arrange-day-${day}`}
                        aria-selected={active}
                        data-day={day}
                        className={
                          active
                            ? "daytabs__tab daytabs__tab--on"
                            : dropDay === day
                              ? "daytabs__tab daytabs__tab--drop"
                              : "daytabs__tab"
                        }
                        onClick={() => selectArrangeDay(day)}
                      >
                        <span className="daytabs__label">Ngày {day}</span>
                        <span className={count === 0 ? "daytabs__count daytabs__count--empty" : "daytabs__count"}>
                          {count === 0
                            ? "Chưa có điểm"
                            : span === null
                              ? `${count} điểm`
                              : `${count} điểm · ~${formatKm(span)}`}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="daytabs__step"
                  aria-label="Ngày sau"
                  disabled={activeArrangeDay >= dayCount}
                  onClick={() => selectArrangeDay(activeArrangeDay + 1)}
                >
                  ›
                </button>
              </div>

              <div className="arrange__board">
                <div className="arrange__panes">
                  {/*
                    The open day, as its own column: where it starts, whether it comes home at noon, and the
                    places on it in the order they will be visited.

                    That order was always the arrangement's — the map drew it and numbered the pins — and there
                    was nowhere to read it and no way to change it except by taking places off the day and
                    tapping them back on in the sequence you wanted, which only works while you remember the
                    sequence. The order decides the day's driving total, so it is a decision the screen owed a
                    control of its own.
                  */}
                  <section
                    className="arrange__day"
                    id={`arrange-day-${activeArrangeDay}`}
                    role="tabpanel"
                    aria-labelledby={`arrange-tab-${activeArrangeDay}`}
                  >
                    <h3 className="arrange__day-head">Ngày {activeArrangeDay}</h3>

                    {/*
                      The day's two settings, on one row.

                      The departure and the midday return were two blocks stacked with a heading each, and the
                      lodging the trip uses — which decides where every later day begins — had a third above the
                      board. Together they pushed the first place about 150px down the screen, which is a
                      measurement and not an impression: what the user came to look at was below the fold behind
                      three answers they had already given. They are one row now, and the row wraps rather than
                      squeezing the select.
                    */}
                    <div className="arrange__config">
                    {/*
                      Where day 1 starts, which the screen has been promising without offering.

                      "Chỗ chạm đầu tiên là nơi ngày bắt đầu — hoặc từ chỗ ở của đêm trước, nếu ngày đó có" has
                      a third case the control never existed for: arriving. Day 1 has no previous night, so
                      without this its anchor is simply absent — the map's total for day 1 omits the drive from
                      the station to the first stop, while every later day includes its own starting leg, and
                      the numbers the user compares across days are not the same number.

                      Its candidates are *all* the user's places rather than the destination's, and that is the
                      point: the bus station or the airport is usually filed outside the area the trip is about.
                      Which is also why it needs grouping — a lodging arrives in that list sorted among forty
                      restaurants, and the bed is what a person looks for first.
                    */}
                    {activeArrangeDay === 1 && otherPlaces.length > 0 && (
                      <label className="field arrange__config-field">
                        <span className="field__label">🚉 Xuất phát từ</span>
                        <select
                          className="field__input"
                          value={entryPlaceId ?? ""}
                          onChange={(event) => setEntryPlaceId(event.target.value === "" ? null : event.target.value)}
                        >
                          <option value="">— từ chỗ đầu tiên của ngày —</option>
                          {entryStays.length > 0 && (
                            <optgroup label="Chỗ ở của chuyến">
                              {entryStays.map((place) => (
                                <option key={place.id} value={place.id}>
                                  {displayName(place.name)}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup label="Điểm tham quan, quán ăn">
                            {entryRest.map((place) => (
                              <option key={place.id} value={place.id}>
                                {displayName(place.name)}
                              </option>
                            ))}
                          </optgroup>
                        </select>
                      </label>
                    )}

                    {/*
                      Where a later day starts, stated and not offered.

                      Night `d - 1` ends day `d`, so the start of every day after the first is the bed the user
                      chose for the night before. A select here would be a second control for one answer, and it
                      would silently disagree with the nights.
                    */}
                    {activeArrangeDay > 1 && (
                      <p className="arrange__start">
                        {lodgingOfNight(activeArrangeDay - 2) === null
                          ? `Đêm trước ngày ${activeArrangeDay} chưa có chỗ ở, nên ngày này chưa có điểm bắt đầu.`
                          : `Bắt đầu từ ${placeNameOf(lodgingOfNight(activeArrangeDay - 2))}.`}
                      </p>
                    )}

                    {/*
                      The midday return, as a switch on the day rather than something the user has to model.

                      A Vietnamese day out is often two: a few places in the morning, back to the room for the
                      hot hours, out again. Asking them to express that with fake places — a "rest" pin they
                      have to drop somewhere — would be asking them to work around the planner. It is a real
                      leg, so it is a real stop, and the day's driving total includes the drive home.
                    */}
                    {chosenStays.length > 0 && (
                      <label className="check arrange__rest">
                        <input
                          type="checkbox"
                          checked={restDays.includes(activeArrangeDay)}
                          onChange={() => toggleRestDay(activeArrangeDay)}
                        />
                        <span>
                          Nghỉ trưa
                          <span className="arrange__rest-note">về chỗ ở, rồi quay lại</span>
                        </span>
                      </label>
                    )}
                    </div>

                    {dayStopIds.length === 0 ? (
                      <p className="field__help">
                        Ngày này chưa có chỗ nào. Chạm vào một chỗ trong kho để thêm vào ngày.
                      </p>
                    ) : (
                      <ol className="arrange__stops">
                        {dayStopIds.map((placeId, index) => {
                          const place = placeById.get(placeId);
                          const chosenSlot = slots[`${activeArrangeDay}|${placeId}`];
                          /*
                           * Only the times this place suits, plus the one already stored.

                           * A place's own `best_time` answers what it is good for, so offering every slot would
                           * invite a schedule the user's own data argues against — and a place that recorded
                           * nothing offers all five, because absence is not a restriction. The stored one is
                           * kept in the list even when it has stopped suiting: filtering is an affordance about
                           * what to *offer*, not a claim about what is in force, and a time nothing displayed
                           * and nothing could clear was a value the plan obeyed and the screen did not show.
                           */
                          const suitedSlots = slotsSuitedBy(bestTimeOf(placeId));
                          const offeredSlots =
                            chosenSlot !== undefined && !suitedSlots.includes(chosenSlot)
                              ? [chosenSlot, ...suitedSlots]
                              : suitedSlots;
                          const name = place === undefined ? "Chỗ đã bị xoá" : displayName(place.name);
                          /*
                           * The drive that reaches this row, priced by the provider.
                           *
                           * Above the row rather than under the one before it, because that is what the number
                           * describes — the cost of getting here — and it puts the first row's drive from the
                           * day's start in the same place as every other row's. Null when the provider priced
                           * no such leg, which is the honest answer for a drive it did not measure.
                           */
                          const drive = legIntoRow[index] ?? null;
                          return (
                            <li
                              key={placeId}
                              ref={(node) => {
                                stopRows.current[index] = node;
                              }}
                              className={
                                drag?.placeId === placeId
                                  ? "arrange__stop arrange__stop--dragging"
                                  : "arrange__stop"
                              }
                            >
                              {dropAt === index && <div className="arrange__drop-line" aria-hidden="true" />}
                              {drive !== null && (
                                <p className="arrange__leg">
                                  {copy.planner.legDrive(formatKm(drive.distanceM), formatDriving(drive.durationS))}
                                </p>
                              )}
                              <div
                                className="arrange__stop-row"
                                onPointerDown={(event) => {
                                  /*
                                   * The whole row is a handle, and that is what makes the gesture findable.
                                   *
                                   * The numbered circle alone read as a label for the position rather than as a
                                   * control — a reviewer asked for reordering that was already there, which is
                                   * what a hidden affordance looks like from the outside. A row is what a hand
                                   * reaches for. Presses that start on one of the row's own buttons are left
                                   * alone: the time chips and the way off the day have their own meanings, and
                                   * a drag that began on one of them would be a drag the user did not start.
                                   *
                                   * `instanceof Element` rather than a cast: the target of a pointer event in
                                   * the DOM is always an element, and a check says so without asserting it.
                                   */
                                  const pressed = event.target;
                                  if (!(pressed instanceof Element) || pressed.closest("button") !== null) return;
                                  beginDrag(event, { kind: "stop", placeId });
                                }}
                              >
                                {/*
                                  A handle that looks like one, and the position beside it as text.

                                  The number and the handle have been through every arrangement of themselves: two
                                  glyphs side by side, which is a duplicated control; then one circle holding the
                                  number, which is a single control that reads as a label for the position — the
                                  user asked for reordering that had been working for two days, which is what a
                                  hidden affordance looks like from the outside.

                                  So they are separate again, with separate jobs: the six dots are the handle and
                                  look like every other drag handle, and the number is content — a small numeral
                                  before the name, matching the numbering the map's pins carry. Nothing is
                                  duplicated because nothing is doing the same thing twice.
                                */}
                                <button
                                  type="button"
                                  className="arrange__grip"
                                  aria-label={`Đổi thứ tự ${name}`}
                                  onPointerDown={(event) => beginDrag(event, { kind: "stop", placeId })}
                                  onKeyDown={(event) => moveStopByKeyboard(event, index)}
                                >
                                  <span className="arrange__grip-dots" aria-hidden="true">
                                    ⠿
                                  </span>
                                </button>
                                <span className="arrange__stop-order">{index + 1}</span>
                                <span className="arrange__stop-name">{name}</span>
                                <button
                                  type="button"
                                  className="arrange__remove"
                                  onClick={() => togglePlaceDay(placeId, activeArrangeDay)}
                                >
                                  Bỏ
                                </button>
                              </div>
                              <div className="chips">
                                {offeredSlots.map((slot) => (
                                  <button
                                    key={slot}
                                    type="button"
                                    className={
                                      chosenSlot === slot ? "chip chip--sm chip--on" : "chip chip--sm arrange__slot"
                                    }
                                    aria-pressed={chosenSlot === slot}
                                    onClick={() => setPlaceSlot(placeId, activeArrangeDay, slot as TripSlot)}
                                  >
                                    {slot}
                                  </button>
                                ))}
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                    )}

                    {/*
                      The store, one tap away instead of a third block right below the day.

                      It was the third thing in the step, under the day and its map, which made it what a user
                      scrolled past to reach — and, on the way, what pushed the two above it off the screen. A place
                      is added *to a day*, so the way to the places belongs at the end of the day's own list, and
                      the list is what is in front of you when you want one more.
                    */}
                    <button type="button" className="button arrange__more" onClick={() => setStoreOpen(true)}>
                      {unplacedCount > 0
                        ? `+ Thêm địa điểm từ kho (còn ${unplacedCount} chỗ)`
                        : "+ Thêm địa điểm từ kho"}
                    </button>
                  </section>

                  {/*
                    The day's road, drawn as the user builds the day.

                    It answers the question this screen could only ask in words — "how far is this day" — at the
                    moment the answer can still change what they do. The stops are numbered by their position in
                    the order the user arranged, which is the order the list beside the map renders, so pin k is
                    row k — and that is the line that passes through them.
                  */}
                  <div className="arrange__road">
                    {/*
                      The day's total above the map rather than under it.

                      Measured in the browser: the map is 60dvh from 760px up, so a sentence below it sits past
                      the fold on a laptop — the number the step exists to let a user reason about was the one
                      thing not on the screen. Above it reads as the map's own caption, which is what it is.
                    */}
                    {/*
                      The suggestion, above the map for the reason the total is: at 60dvh the map pushes
                      anything under it past the fold, and a suggestion nobody scrolls to is not a suggestion.

                      And the undo, in the same box: once the suggestion has been taken the box is no longer
                      offering anything, it is reporting what happened and offering the one tap back. The undo
                      takes the box over rather than standing beside it — the suggestion goes away by itself
                      once the day has been measured again, so a box that only ever spoke about
                      `arrangeSuggestion` would take the way back with it (see `SuggestionUndo`). The undo is
                      drawn on the day it was taken on, and it is gone the moment that day moves for any other
                      reason.
                    */}
                    {suggestionUndo !== null && suggestionUndo.day === activeArrangeDay ? (
                      <div className="arrange__suggest">
                        <p className="arrange__suggest-text">{copy.planner.reorderUndoNote}</p>
                        <button type="button" className="button" onClick={undoShortestOrder}>
                          {copy.planner.reorderUndoAction}
                        </button>
                      </div>
                    ) : arrangeSuggestion !== null ? (
                      <div className="arrange__suggest">
                        <p className="arrange__suggest-text">
                          {copy.planner.reorderNumbers(
                            formatKm(arrangeSuggestion.shortestM),
                            formatKm(arrangeSuggestion.asArrangedM),
                          )}
                        </p>
                        <button type="button" className="button" onClick={applyShortestOrder}>
                          {copy.planner.reorderAction}
                        </button>
                      </div>
                    ) : null}
                    {arrangeBoard.stops.length >= 2 ? (
                      /*
                       * The day's total, as a card on the map rather than a line above it.
                       *
                       * It is the map's own caption — the number that says what the line costs — and above the map
                       * it was a row of text spending 24px of the height the map needed. Floating it inside the
                       * map's own box gives that height back, which is what the brief asked for and what the
                       * sticky column can now use.
                       */
                      <div className="arrange__map">
                        {arrangeRoadNote !== "" && <p className="arrange__road-note">{arrangeRoadNote}</p>}
                        <RouteMap
                          stops={arrangeMapStops}
                          geometry={arrangeRoad.data?.geometry ?? null}
                          legs={arrangeMapLegs}
                          distancesKnown={arrangeRoad.data?.distances_known === true}
                          routingPending={arrangeRoad.data === undefined}
                        />
                      </div>
                    ) : (
                      arrangeRoadNote !== "" && (
                        <p className="arrange__road-note-flow">{arrangeRoadNote}</p>
                      )
                    )}
                  </div>

                  {/*
                    The store: everything a day could still have, in the sections the picker uses.

                    One grouping rule for both screens — `groupPlaces` — because a place that is "Cà phê" one
                    step earlier and unlabelled here is the same list pretending to be two.

                    Each row is a checkbox and a drag source. The checkbox is the whole row and the drag starts
                    on the tile, which is why the tile is not a link here: on a phone a drag and a tap begin with
                    the same finger down, and the row has to be both without being ambiguous — a tap anywhere
                    toggles, a drag from the tile moves.
                  */}
                </div>
              </div>
              {/*
                The store as a slide-over.

                Deliberately **not** modal: no scrim, no focus trap. The rail behind it is a drop target — a place
                is dragged from a row onto a day — and a scrim would put the gesture's destination behind a sheet.
                It is also how the counts stay live while it is open, which is the feedback that says the tick
                landed.
              */}
              {storeOpen && (
                <div ref={storeDrawerRef} className="store-drawer" role="dialog" aria-label="Kho địa điểm">
                  <div className="store-drawer__bar">
                    <button type="button" className="store-drawer__close" onClick={() => setStoreOpen(false)}>
                      Đóng
                    </button>
                  </div>
                      {/*
                        The store's heading, with the step's count beside it.

                        The count belongs where the ticks happen: this is the list every tick comes from, and it is
                        also the row the count is read against — "three places left" is a fact about these rows. It
                        sat in the title's row and cost that row its line on a 390px screen, which put the two small
                        ways out of the step on a line of their own. Measured with it there: the actions landed at
                        y=68 under a title at y=23.
                      */}
                      <div className="store-drawer__head">
                        <h3 className="store-drawer__title">Kho địa điểm</h3>
                        <p
                          className={
                            unplacedCount === 0 && emptyDays.length === 0
                              ? "arrange__status arrange__status--clear"
                              : "arrange__status"
                          }
                          role="status"
                        >
                          {unplacedCount === 0 && emptyDays.length === 0
                            ? `Đủ ${selectedPlaces.length} chỗ`
                            : [
                                unplacedCount > 0 ? `${unplacedCount}/${selectedPlaces.length} chưa xếp` : "",
                                emptyDays.length > 0 ? `${emptyDays.length} ngày trống` : "",
                              ]
                                .filter((part) => part !== "")
                                .join(" · ")}
                        </p>
                      </div>
                      <div className="picker__list picker__list--grouped">
                        {groupPlaces(selectedPlaces).map(({ group, places: groupPlacesInSection }) => (
                          <div key={group.key} className="picker__group">
                            <h4 className="picker__group-heading">
                              {group.glyph} {group.label}
                              <span className="picker__group-count">{groupPlacesInSection.length}</span>
                            </h4>
                            {groupPlacesInSection.map((place) => {
                              const onThisDay = dayStopIds.includes(place.id);
                              /*
                               * The other days this place is already on.
                               *
                               * The repeat is invisible from inside one day — you tick a café on day 1, move to
                               * day 2, and the screen looks exactly as it did before. Naming the days it is also
                               * on is what keeps a repeat a decision rather than a surprise on the itinerary.
                               */
                              const otherDays = Array.from({ length: dayCount }, (_, index) => index + 1).filter(
                                (day) => day !== activeArrangeDay && (arrangement[day] ?? []).includes(place.id),
                              );
                              const away = straightLineFromDayStart(place);
                              return (
                                <label
                                  key={place.id}
                                  className="picker__row"
                                  onClickCapture={(event) => {
                                    /*
                                     * The click a drag ends on is not also a tap.
                                     *
                                     * A pointer released over this row ends a drag, and the browser then delivers
                                     * a click to whatever is under it — which, on a row whose whole body is a
                                     * checkbox label, would tick the place onto the day the user just dragged it
                                     * away from.
                                     */
                                    if (afterDrag.current) {
                                      afterDrag.current = false;
                                      event.preventDefault();
                                      event.stopPropagation();
                                    }
                                  }}
                                >
                                  <span
                                    className={`place-card__tile place-card__tile--sm ${tileTintOf(place.categories)}`}
                                    aria-hidden="true"
                                    onPointerDown={(event) => beginDrag(event, { kind: "store", placeId: place.id })}
                                  >
                                    <span className="place-card__glyph">{groupOf(place.categories).glyph}</span>
                                  </span>
                                  <input
                                    type="checkbox"
                                    checked={onThisDay}
                                    onChange={() => togglePlaceDay(place.id, activeArrangeDay)}
                                  />
                                  <span className="picker__row-body">
                                    <span className="picker__row-name">{displayName(place.name)}</span>
                                    <span className="picker__row-meta">
                                      {away === null ? group.label : `${group.label} · ~${formatKm(away)}`}
                                    </span>
                                  </span>
                                  {otherDays.length > 0 && (
                                    <span className="picker__row-also">Ngày {otherDays.join(", ")}</span>
                                  )}
                                  {/*
                                    A place with no coordinates can be arranged and cannot be routed.

                                    It matters most here: the arrange step is where a place is put on a day, so it
                                    is where the user can still do something about a stop the planner will have to
                                    leave out.
                                  */}
                                  {place.lat === null && <span className="picker__row-flag">⚠ Chưa có toạ độ</span>}
                                </label>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                  
                </div>
              )}
            </>
          )}

          {/*
            A generation that failed while the user was still arranging.

            The button below starts the run, so a failure has to be readable on the step that started it —
            dropping the user on the review step to find the alert there would move them away from the
            arrangement they may want to change. The message is the API's, mapped by `messageFor`; the way
            forward is the button, which restarts the run, and the head's back control.
          */}
          {generatePlans.isError && (
            <div className="alert alert--danger" role="alert">
              {messageFor(generatePlans.error)}
            </div>
          )}

          {/*
            The pinned bar, and not `savebar--static`.

            Measured at 390x844 with the day board filled: the board alone is 1067px, so this bar started at
            document y 1326 and its button ended 572px below the fold — the one control that leaves the step
            was the one control the step could not show. The arrangement is put together over a long scroll,
            and this button has to be reachable at the end of it without hunting. Measured after: the button sits
            at the bottom of the scrollport at every width, and the board is 1133px in the same window.

            The map was the reason this bar had been left in the flow, and it is not one any more. At one
            column — the phone, which is where the step is used — the map is a pane *below* the day's list, so
            what passes under the bar is text. At two columns the map does sit beside that list, and its foot
            reached under the bar and covered the Leaflet attribution, which the OpenStreetMap tile usage
            policy requires to be visible; the height rule in `screens.css` now subtracts the bar, so the map
            ends above it. Measured at 1366x900: map bottom 747 against a bar top of 823.
          */}
          <div className="savebar plan-actions" ref={measureSavebar}>
            <button
              type="button"
              className="button button--primary"
              disabled={generatePlans.isPending}
              onClick={() => {
                /*
                 * The act is generation, and the step is where the result is read.
                 *
                 * The arrangement lives on the trip, so this press persists it on the way through — but the
                 * press is not a save any more, it is the request for a plan, which is why it and the step
                 * that shows one are the same action. The stale set goes first: a plan built from an
                 * arrangement the user has since changed is not the answer, and leaving it in state would
                 * put the previous itinerary on the review step beside the line saying a new one is coming.
                 * Generation's own pending state carries the button through a second press, and the review
                 * step owns loading, failure and retry — one run, one status, wherever it is read.
                 */
                setVariants([]);
                setVariantDetails({});
                setExplanations({});
                generatePlans.mutate();
                setStep(3);
              }}
            >
              {copy.planner.createPlan}
            </button>
            {/*
              What the button's label used to carry.

              The count of empty days was a parenthetical inside "Tiếp: Thiết lập ràng buộc", so the destination
              and the warning were one string and the button described a problem as well as a place. They are
              two things now: the label names the step and this line stands under it, on its own row, so the
              count never moves the button. Given the whole row it is one line whatever the count, so the bar's
              height changes when the line appears or leaves and never when the number does.

              Not rendered at all when every day has a place: "Còn 0 ngày trống" would be a warning about
              nothing, and an empty reserved row would leave a gap under a bar that has nothing to warn about.
            */}
            {emptyDays.length > 0 && (
              <p className="plan-actions__note">{copy.planner.emptyDaysNote(emptyDays.length)}</p>
            )}
          </div>
        </div>
      )}

      {/*
        The constraint step (S09) used to sit here, offering `wake_time`, `check_in_time` and
        `max_driving_minutes`. Removed 2026-10-08, by measurement rather than taste: `wake_time` and
        `check_in_time` only shift clock times after the stops and their order are already fixed,
        `max_driving_minutes` only appends a warning and never trims, reorders or drops a stop, and under a
        user arrangement all three are inert because the engine bypasses the arrangement entirely. The
        published request fields, their columns and their defaults stay (see `TripConstraints` in
        `@trippool/contracts`), because removing a field from a published request is a breaking change and the
        plan handler reads them from the trip's own row. Only the interface stops offering them.

        The pace chooser stays where it is — step 1, not here — because it is the one control the measurement
        shows changing a plan at all (5/4 to 3/3 days, 9 stops to 6); removing it would hard-code a density
        for every trip.
      */}

      {/* ======================= STEP 3: THE PROPOSED PLAN ======================= */}
      {step === 3 && (
        <div className="plan-step">
          {/*
            The run asked for on the way in.

            Arriving at this step is what asks for a plan, so this is the line that says one is being built —
            named for the act ("a plan is being made") rather than "Đang tải", because the wait is planning
            and not fetching. It is the same mutation the arranging step called; whichever step is on screen,
            this is its one status.
          */}
          {generatePlans.isPending && <p className="state">{copy.planner.generatingPlan}</p>}
          {/*
            A generation that failed, and the way out of it.

            The alert is the explanation and the retry is the way forward, on the step that now owns
            generation: a resumed draft, or a second attempt after a provider failure, must not need a trip
            back to the arrangement to try again. While this is the state there are no variants to review, so
            the retry is the step's only action until one arrives — or the user leaves by the head's back
            control, which is always there.
          */}
          {generatePlans.isError && (
            <div className="alert alert--danger" role="alert">
              {messageFor(generatePlans.error)}
            </div>
          )}
          {generatePlans.isError && (
            <button type="button" className="button" onClick={() => generatePlans.mutate()}>
              {copy.state.retry}
            </button>
          )}

          {/*
            A failed selection must not look like a dead button: the variant may have been marked
            server-side while its detail could not load, so the user retries rather than double-taps
            into silence.
          */}
          {openVariant.isError && (
            <div className="alert alert--danger" role="alert">
              {messageFor(openVariant.error)}
            </div>
          )}

          {/*
            AC-6.1: when the routing provider did not answer, the honest screen says so instead of
            letting "chưa xác định" look like an outage the user caused. The plan's shape is still
            real; only its measurements are missing.
          */}
          {routingStatus !== null && routingStatus !== "ok" && (
            <div className="alert alert--warning" role="alert">
              {copy.planner.routingUnavailable}
            </div>
          )}

          {/*
            The review, and — only without an arrangement — the strategy choice.

            With an arrangement the three strategies produce identical stops, order, distance and day split,
            differing only in rest seconds (measured 2026-10-02), so this step is a review of the whole route
            day by day with one confirm. Without one they genuinely differ, and the chip row lets the user
            choose which strategy's plan to review; the pace's own strategy is the default, not the only
            answer.

            One card rather than one per variant: it always shows the selected strategy's full itinerary
            (`shownStrategy` above), and the single confirm below commits it. That is what keeps the two
            states one screen — the choice changes which plan is reviewed, not how it is reviewed.
          */}
          {!hasArrangement && variants.length > 1 && (
            <div className="preset-chips" role="group" aria-label={copy.planner.strategyLabel}>
              {variants.map((variant) => {
                const on = planVariant?.strategy === variant.strategy;
                return (
                  <button
                    key={variant.id}
                    type="button"
                    className={`chip ${on ? "chip--on" : ""}`}
                    aria-pressed={on}
                    onClick={() => setStrategyPick(variant.strategy)}
                  >
                    {variant.label}
                  </button>
                );
              })}
            </div>
          )}

          {planVariant !== null && (
            <div className="plan-variant">
              <div className="plan-variant__head">
                <h3 className="plan-variant__title">{planVariant.label}</h3>
                {/*
                  Said only when it is true: the same badge would be a lie on the fallback plan, which is
                  shown precisely because the engine did not offer the strategy this pace names.
                */}
                {planVariant.strategy === paceStrategy && (
                  <span className="badge">{copy.planner.matchesPace}</span>
                )}
              </div>

              {/*
                The itinerary itself, because a total is not a trip.

                This card used to show four numbers and nothing else, and two plans that visit the same
                places in a different order have the same four numbers. The chain is what the plan actually
                is; the figures stay below it as the summary of the chain rather than as the whole of it.
                Every day is listed, so the whole route is read before the one confirm.
              */}
              {/*
                The proposed itinerary: Stacked Day Blocks with timeline on the left and day map on the right.
              */}
              {shownPlanDetail !== undefined && (
                <PlanRoadmap
                  key={shownPlanDetail.variant.id}
                  detail={shownPlanDetail}
                  startDate={startDate}
                  hideSummary={true}
                />
              )}

              {/*
                Every figure comes from the variant the planner stored; null renders unmeasured.

                The same row the comparison used, kept as the plan's own summary. It is one set of numbers
                now rather than three side by side, but a row is still how they are read: as a run-on line
                they are a sentence to parse.
              */}
              <dl className="figures">
                {shownPlanNumbers.map((number) => (
                  <div key={number.label} className="figure">
                    <dd className="figure__value">{number.value}</dd>
                    <dt className="figure__label">{number.label}</dt>
                  </div>
                ))}
              </dl>

              {planVariant.unsatisfied.length > 0 && (
                <div className="alert alert--warning" role="alert">
                  {copy.planner.unsatisfied} {planVariant.unsatisfied.join("; ")}
                </div>
              )}

              {shownPlanExplanations.length > 0 && (
                <div className="plan-variant__reasons">
                  {shownPlanExplanations.map((explanation) => (
                    <p key={explanation.reason} className="plan-variant__reason">
                      &ldquo;{explanation.reason}&rdquo;
                    </p>
                  ))}
                </div>
              )}

              {/*
                The commit, and the words say so.

                Selecting a plan is a server write: `selectPlan` is what moves the trip from `draft` to
                `planned`, and the trips list then reads "Đã có lộ trình" for it. So this is not "choose",
                which names a screen state — it is the moment the plan is locked and saved, and the label
                names that. `openVariant` then loads the chosen variant's days and moves to the roadmap.
              */}
              <button
                type="button"
                className="button button--primary"
                disabled={openVariant.isPending}
                onClick={() => openVariant.mutate(planVariant.id)}
              >
                {openVariant.isPending ? copy.planner.confirmingPlan : copy.planner.confirmPlan}
              </button>
            </div>
          )}

        </div>
      )}

      {/* ======================= STEP 4: ROADMAP ======================= */}
      {step === 4 &&
        (planDetail === null ? (
          /*
           * The roadmap with nothing to draw, said rather than shown as a blank.
           *
           * Unreachable through the screen as it stands — the step is set only once the chosen variant's
           * detail has arrived — and it was reached anyway, by a button that wrote step 4 while generating,
           * which is why this exists. A step that renders nothing cannot be told from a broken app, and the
           * user's report of it was a screenshot with no words in it to search for.
           */
          <div className="state">
            <p className="state__desc">{copy.planner.nothingToDraw}</p>
            <button type="button" className="button" onClick={() => setStep(3)}>
              {copy.planner.backToPlan}
            </button>
          </div>
        ) : (
        <>
            {/*
              Keyed by variant: which day is being read is state inside `PlanRoadmap`, and a plan whose
              days were reordered can leave the previous index past the end of this one. Remounting is
              the reset, rather than an effect someone can forget to keep.
            */}
            <PlanRoadmap key={planDetail.variant.id} detail={planDetail} startDate={startDate} />

            <div className="savebar savebar--static plan-actions">
              {/*
                No back control here either: it went to step 3, which is what the head's "Quay lại" already
                does, so the bar held two ways to the same place on one screen. The bar keeps its one action.
              */}
              <button
                type="button"
                className="button button--primary"
                onClick={() => navigate(`/trips/${planDetail.trip.id}`)}
              >
                {copy.trips.openTrip}
              </button>
              {/*
                "Mở" rather than "Lưu": the trip and its plans were written to the server by the two calls
                above, so the old button claimed credit for work already done and then went to a list that
                showed nothing. It goes where the itinerary now lives.
              */}
            </div>
        </>
        ))}

      {/*
        The reset confirm.

        Rendered at the screen's own level rather than inside the head, because the head is where the chip is
        and a dialog inside a 60px row would be laid out against it. `position: fixed` on the scrim takes it
        out of the flow, so it neither adds a gap at the end of this column nor scrolls.

        The pattern is the trips list's, deliberately: same scrim, same panel, same two actions in the same
        order, so a user who has deleted a trip recognises this dialog as the one that asks before it takes
        something away. The scrim itself closes it — a tap outside a confirm means "no", which is the
        cautious reading and the one that costs nothing.
      */}
      {resetOpen && (
        <div
          className="places-page__modal-scrim"
          onClick={() => setResetOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-reset-planner-title"
        >
          <div className="places-page__modal" onClick={(event) => event.stopPropagation()}>
            <h2 id="confirm-reset-planner-title" className="places-page__modal-title">
              {tripTitle === "" ? copy.planner.confirmResetUnnamed : copy.planner.confirmReset(tripTitle)}
            </h2>
            <p className="places-page__modal-desc">{copy.planner.resetWarns}</p>
            <div className="places-page__modal-actions">
              <button type="button" className="button button--ghost" onClick={() => setResetOpen(false)}>
                {copy.capture.cancel}
              </button>
              <button
                type="button"
                className="button button--danger"
                onClick={() => {
                  setResetOpen(false);
                  resetPlanner();
                }}
              >
                Đặt lại
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
